import dayjs from "dayjs";
import { delay, http, HttpResponse, RequestHandler } from "msw";
import { z } from "zod";
import { DaylightDateFormat } from "../constants/DaylightDateFormat";
import { filterArrayBySearchTerm } from "../constants/filters/utils";
import { HandlerConfig } from "../devtools/types";
import type { FuelScaleDetail } from "../hooks/react-query/backOffice/useFuelScales";
import { RouteStatus } from "../hooks/react-query/dispatch/useRoutes";
import { AssignStopResponse } from "../hooks/react-query/dispatch/useStops";
import type { TripNote } from "../hooks/react-query/linehaul/useTripNotes";
import { Note } from "../hooks/react-query/outbound/linehaulTrailers/useLoadTrailerNotes";
import { User } from "../hooks/react-query/settings/security/useUsers";
import { AddToPlanResponse } from "../hooks/react-query/useAddToPlan";
import type { DeliveryRouteSequenceStatus } from "../hooks/react-query/useDeliveryRoutesSequenceStatus";
import { ucccOfdShipmentsSchema } from "../pages/linehaul/ucccOfd/ucccOfdShipmentRow.type";
import {
  EquipmentType,
  EquipmentTypeId,
  equipmentTypeMap,
} from "../types/equipment.type";
import type { OsadAssignment } from "../types/operations/osad.type";
import { LaneType } from "../types/outbound/lane.type";
import { Trap, trapSchema } from "../types/planning/trap.type";
import { apiUrls } from "../utils/apiUrls";
import { mockEquipmentData } from "./equipment/equipments.mocks";
import {
  mockAppointmentBucketTableData,
  mockPlannedTraps,
  mockRoutesSummaryData,
  mockSectionsData,
  mockShipmentData,
  mockTrapSummaryData,
} from "./inbound/prePlanning/prePlanning.mocks";
import { mockEntryDetails } from "./linehaul/entry.mocks";
import { mockLinehaulCarriers } from "./linehaul/linehaulCarriers.mocks";
import { mockMasterScheduleList } from "./linehaul/masterSchedule.mocks";
import { mockMasterScheduleTemplates } from "./linehaul/masterScheduleTemplates.mocks";
import { publishResponseMocks } from "./linehaul/publishResponses.mock";
import { mockServiceTypes } from "./linehaul/serviceTypes.mocks";
import {
  mockServiceFailureReasonCodes,
  mockUcccOfdDashboardSummary,
  mockUcccOfdShipments,
  serializeUcccOfdShipmentsListForApi,
} from "./linehaul/ucCcOfdShipments.mocks";
import {
  formatUrl,
  getNextAutoIncValue,
  getTrapNameFromId,
  getUrlParam,
} from "./mockUtils";
import { linehaulTrailersResponse } from "./outbound/LinehaulTrailers.mocks";
import { linehaulTripsResponse } from "./outbound/LinehaulTrips.mocks";
import { loadTrailerNotesResponse } from "./outbound/LoadTrailerNotes.mocks";
import { mockPresetLanesList } from "./outbound/PresetLanes.mocks";
import { shipmentSearchResponse } from "./outbound/ShipmentsSearch.mocks";
import {
  companyOwnedServiceCenters,
  mockDrivers,
  mockDriversUsage,
  mockServiceCenterDoors,
  serviceCenterDoorNumbers,
} from "./serviceCenter/serviceCenters.mocks";

import { shippingInstructionOptions } from "./backOffice/clientServices/pickUpEntry.mocks";
import {
  mockCriteriaGroupLogicOperators,
  mockCriteriaLogicOperators,
  mockFieldNamesByTable,
  mockLookupTypesByTable,
  mockTableNameOptions,
} from "./backOffice/criteriaOptions.mocks";
import {
  mockFuelPriceApiResponse,
  mockFuelPriceHistoryResponses,
} from "./backOffice/fuelPrice.mocks";
import {
  mockFuelPriceListOptions,
  mockFuelScaleCriteria,
  mockFuelScales,
  mockFuelSurchargeCodeDetails,
  mockServiceLevelOptions,
} from "./backOffice/fuelScales.mocks";
import { mockBills } from "./backOffice/billing.mocks";
import { mockFuelSurcharges } from "./backOffice/globalRates.mocks";
import {
  mockLaneMinimumList,
  mockLocationTypes,
  mockLocationValuesByType,
} from "./backOffice/laneMinimumList.mocks";
import {
  mockLaneMinimumSchedules,
  mockLaneMinimumScheduleDetail,
} from "./backOffice/laneMinimumSchedules.mocks";
import {
  mockAdditionalServices,
  mockBackOfficeAccounts,
  mockBackOfficeLocations,
  mockCommodityClassOptions,
} from "./backOffice/quickQuote.mocks";
import { mockQuickQuoteSummaryData } from "./backOffice/quickQuoteSummary.mocks";
import {
  mockShipments,
  mockShipmentsStatusCounts,
} from "./backOffice/shipments.mocks";
import { mockBillers } from "./backOffice/billing.mocks";
import { mockDriverLatestLocations } from "./dispatch/drivers.mock";
import { mockInsightsDrivers } from "./dispatch/insightsDrivers.mock";
import { mockNotifications } from "./dispatch/notifications.mock";
import {
  mockDispatchAvailableRouteStatuses,
  mockDispatchRouteDetail,
  mockDispatchRoutes,
  mockDispatchSuggestedRoutes,
  mockDispatchSuggestedStops,
} from "./dispatch/routes.mock";
import {
  defaultShipmentNotes,
  mockShipmentDetails,
  mockShipmentDocuments,
  mockShipmentHistory,
  mockShipmentInsights,
} from "./dispatch/shipments.mock";
import {
  mockDispatchStops,
  mockDispatchStopShipmentNotes,
  mockDispatchStopSuggestedSics,
} from "./dispatch/stops.mock";
import { mockSummary } from "./dispatch/summary.mock";
import { centralizedEquipmentBySicMock } from "./equipment/centralizedEquipmentBySic.mocks";
import { mockEquipmentsBySicIdAndTypeData } from "./equipment/equipmentsBySicIdAndType.mocks";
import { mockEquipmentsUsageBySicIdData } from "./equipment/equipmentsUsageBySicId.mocks";
import { mockSearchByRefData } from "./equipment/liveInventory.mocks";
import {
  mockOldPlannedRouteTrapStatuses,
  mockPlannedRouteTrapStatuses,
} from "./inbound/planning/plannedRouteTrapStatus.mock";
import { mockPlannedUpdateRouteTrapStatuses } from "./inbound/planning/plannedUpdateRouteTrapStatus.mock";
import {
  mockPlannedRoutesTraps,
  mockRouteAssignment,
  mockRouteStatuses,
  mockTrapStatuses,
} from "./inbound/planning/planning.mock";
import { mockPrintResult } from "./inbound/planning/printResult.mock";
import {
  mockPlannedShipmentsDetails,
  mockShipmentIds,
  mockUnassignedShipmentIds,
} from "./inbound/planning/shipments.mock";
import {
  mockPresetRoutesAndTraps,
  oldMockPresetRoutesAndTraps,
} from "./inbound/presetRoutesAndTraps.mock";
import { mockIncompleteTrailers } from "./inbound/unplannedFreight/incomplete-trailer-mocks";
import { mockStops } from "./inbound/unplannedFreight/linehaul-stops.mocks";
import {
  mockFodShipments,
  mockIncompleteShipmentIds,
  mockIncompleteShipmentsDetails,
  mockLinehaulTrips,
  mockShuttleTrips,
} from "./inbound/unplannedFreight/unplannedFreight.mocks";
import { mockInboundUrgentAndCriticalCare } from "./inbound/urgentAndCriticalCare/UrgentAndCriticalCare-mocks";
import { mockTripDashboardNotifications } from "./linehaul/alerts.mocks";
import { mockBulkManifestShipments } from "./linehaul/bulkManifest.mock";
import { cancellationReportMock } from "./linehaul/cancellations.mock";
import { mockChangeRequestLogs } from "./linehaul/changeRequestLog.mocks";
import { mockedClosedTripDetails } from "./linehaul/closedTripDetails.mocks";
import { mockDaySummary } from "./linehaul/daySummary.mocks";
import {
  mockDaySummaryV2,
  mockRolloverFeeDetails,
  mockTripDetails,
} from "./linehaul/daySummaryV2.mocks";
import { mockDelayReasons } from "./linehaul/delayReasons.mocks";
import { mockDeliveredTrips } from "./linehaul/deliveredTrips.mocks";
import { mockEmployeeDrivers } from "./linehaul/employeeDrivers.mock";
import {
  cancelledTrips,
  endOfShiftComments,
  laneProjections,
  latestModifications,
  reportHeader,
  unpublishedBookings,
  yardCheck,
  yardCheckFileUpload,
} from "./linehaul/endOfShiftReport.mock";
import { mockEquipment } from "./linehaul/equipment.mock";
import { mockLoadBoardSchedulePair } from "./linehaul/loadBoardSchedulePair.mocks";
import { loadTrips, trailers } from "./linehaul/loadTrips.mocks";
import { mockTransitDashboardSummary } from "./linehaul/mockTransitDashboardSummary";
import { mockRegions } from "./linehaul/regions.mock";
import {
  mockLoadAndHoldReport,
  mockTripsReport,
  mockUrgentCareReport,
  mockVendorComplianceReport,
} from "./linehaul/reports.mocks";
import { mockScacCodes } from "./linehaul/scacCodes.mocks";
import { mockScheduleNotes } from "./linehaul/scheduleNotes.mock";
import { mockShipmentNotes } from "./linehaul/shipmentNotes.mocks";
import {
  mockBulkRequestTripsByCarrier,
  mockInTransitTrips,
  mockRecentUpdates,
  mockTransitDashboardShipments,
  mockTransitDashboardTripDetails,
  mockTransitDashboardTripDetailsWithLoadPair,
  mockTripNotes,
} from "./linehaul/transitDashboard.mocks";
import { mockTripHistory } from "./linehaul/tripHistory.mock";
import { mockTripOptimization } from "./linehaul/tripOptimization.mocks";
import { mockTripsForReassign } from "./linehaul/tripsForReassign.mocks";
import { notifications } from "./linehaul/ucccNotifications.mocks";
import { mockUcccOfdShipmentDetails } from "./linehaul/ucCcOfdShipments.mocks";
import { mockUcccPlanningShipments } from "./linehaul/ucCcPlanningShipments.mocks";
import { mockReasonCodes } from "./operations/reason-codes.mock";
import { mockTripList } from "./operations/trip-list.mock";
import { carriersList } from "./outbound/Carriers.mocks";
import { doorShipmentsResponse } from "./outbound/DoorShipments.mocks";
import { doorLayoutList } from "./outbound/DoorsLayout.mocks";
import { equipmentList } from "./outbound/Equipment.mocks";
import { doorAssets } from "./outbound/LaneDetails/DoorAssets/DoorAssets.mocks";
import { doorsUsageInfo } from "./outbound/LaneDetails/DoorsUsageInfo.mocks";
import { laneDetails } from "./outbound/LaneDetails/LaneDetails.mocks";
import { laneDetailsTonnage } from "./outbound/LaneDetails/LaneDetailsTonnage.mocks";
import {
  dalLaneShipments,
  dfwLaneShipments,
  laneShipments,
} from "./outbound/LaneDetails/ShipmentDetails/LaneShipments.mocks";
import { shipmentDetails } from "./outbound/LaneDetails/ShipmentDetails/ShipmentDetails.mocks";
import { shipmentDetailsHistory } from "./outbound/LaneDetails/ShipmentDetails/ShipmentDetailsHistory.mocks";
import { trapDetails } from "./outbound/LaneDetails/ShipmentDetails/TrapDetails.mocks";
import { mockLanesList } from "./outbound/Lanes.mocks";
import { linehaulTrailerShipmentsResponse } from "./outbound/LinehaulTrailerShipments.mocks";
import { loadTrailerShipmentsResponse } from "./outbound/LoadsTrailerShipments.mocks";
import { loadTrailersResponse } from "./outbound/LoadTrailers.mocks";
import { osadSummaryAndShipments } from "./outbound/OSAD";
import { pndTrailersResponse } from "./outbound/PndTrailers.mocks";
import { mockPresetLanesSicsSuggestionList } from "./outbound/PresetLanesSicsSuggestion.mocks";
import { shipmentTagsResponse } from "./outbound/ShipmentTags.mocks";
import { mockStaticLoadTemplatesList } from "./outbound/StaticLoadTemplates.mocks";
import { trapTrailersResponse } from "./outbound/TrapTrailers.mocks";
import { getMockReport } from "./report";
import { mockServiceCenterGeocode } from "./serviceCenter/geocode.mocks";
import { mockSicsSuggestion } from "./serviceCenter/sicsSuggestion.mocks";
import { mockEmployees } from "./settings/employees.mocks";
import { mockSicContact, mockSicDetails } from "./settings/linehaul-sics.mocks";
import {
  mockAvailableZipCodeWithTags,
  mockCreateZipCodeWithTags,
  mockUpdateZipCodeWithTags,
} from "./settings/zipCodeWithTags.mocks";
// the endpoints for these handlers are deployed and active. More: https://mswjs.io/docs/basics/intercepting-requests#request-pathname

const readNotificationIds = new Set<number>();

export function requestHandlers(config: HandlerConfig): RequestHandler[] {
  return [
    /** Master Schedule */
    http.get(
      formatUrl(apiUrls.masterSchedule.get("*")),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const date = dayjs(url.searchParams.get("weekOf") ?? "");
        const key = date.isSame(dayjs(), "week")
          ? "default"
          : date.isSame(dayjs().add({ week: 1 }), "week")
            ? "deletable"
            : "empty";
        return HttpResponse.json(mockMasterScheduleList[key]);
      }
    ),
    http.post(
      formatUrl(apiUrls.masterSchedule.createTemplateWithClone),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.get(
      formatUrl(apiUrls.masterSchedule.getSchedulePair(":id")),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const id = url.searchParams.get("id");
        const idSegment = url.pathname.split("/").pop();
        const num = Number(idSegment);
        if (!Number.isNaN(num) && String(num) === idSegment) {
          return HttpResponse.json(mockEntryDetails.withStopAndDeadhead);
        }
        if (!id) throw new Error("Missing id parameter");
        return HttpResponse.json(mockEntryDetails.default);
      }
    ),

    http.post(
      formatUrl(
        apiUrls.masterSchedule.publish(
          dayjs().endOf("week").format(DaylightDateFormat.ISO_DATE)
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(publishResponseMocks.default);
      }
    ),

    http.get(formatUrl(apiUrls.masterSchedule.getCarriers), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockLinehaulCarriers.default);
    }),

    http.get(formatUrl(apiUrls.masterSchedule.getServiceTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockServiceTypes.default);
    }),

    http.get(formatUrl(apiUrls.masterSchedule.getTemplates), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockMasterScheduleTemplates.default);
    }),

    /** Inbound Pre-Planning */

    // * Get routes
    http.get(
      formatUrl(
        apiUrls.getDeliveryRoutesSummary(
          ":sicId" as unknown as number,
          ":date" as unknown as number
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const searchTerm = url.searchParams.get("searchTerm");
        return HttpResponse.json(
          filterArrayBySearchTerm(
            mockRoutesSummaryData.default,
            ["title"],
            searchTerm || ""
          )
        );
      }
    ),

    // * Get traps
    http.get(formatUrl(apiUrls.getTraps), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const searchTerm = url.searchParams.get("searchTerm");
      return HttpResponse.json(
        filterArrayBySearchTerm(
          mockTrapSummaryData.default,
          ["title", "trailerNumber"],
          searchTerm || ""
        )
      );
    }),

    http.get(
      formatUrl(apiUrls.getInboundTraps(":sicId", ":date")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockPlannedTraps.default);
      }
    ),

    // * Get appointment buckets
    http.get(
      formatUrl(apiUrls.getAppointmentBucket(":sicId", ":planDate", "*")),
      async ({ request }) => {
        const url = new URL(request.url);
        const searchTerm = url.searchParams.get("searchTerm");
        await delay(config.delay);
        return HttpResponse.json(
          filterArrayBySearchTerm(
            mockAppointmentBucketTableData.default,
            ["title"],
            searchTerm || ""
          )
        );
      }
    ),

    // * Get sections
    http.get(formatUrl(apiUrls.getSectionsTableData), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const searchTerm = url.searchParams.get("searchTerm");
      const destinationSicId = url.searchParams.get("destinationSicId");
      if (!destinationSicId) {
        throw new Error("Missing destinationSicId parameter");
      }
      return HttpResponse.json(
        filterArrayBySearchTerm(
          mockSectionsData.default,
          ["title"],
          searchTerm || ""
        ),
        {
          status: destinationSicId ? 200 : 400,
        }
      );
    }),

    // * Merge Routes Traps
    http.post(formatUrl(apiUrls.mergeRoutesAndTraps), async () => {
      await delay(config.delay);
      return new HttpResponse(null, { status: 204 });
    }),

    // * Merge Routes
    http.patch(
      formatUrl(apiUrls.mergeRoutes(":routeId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 204 });
      }
    ),

    // * Delete Routes
    http.delete(
      formatUrl(apiUrls.deleteRouteAssignments(":idsToDelete")),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 200 });
      }
    ),

    /** Global */
    // * Get service centers
    http.get(formatUrl(apiUrls.getServiceCenters), async () => {
      await delay(config.delay);
      return HttpResponse.json(companyOwnedServiceCenters);
    }),

    http.get(
      formatUrl(
        apiUrls.getServiceCenterGeocodeByIds([":sicIds" as unknown as number])
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockServiceCenterGeocode.default);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.sicsSuggestion(
          ":sicCode",
          ":destinationSicId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockSicsSuggestion.default);
      }
    ),

    // * Create Trap
    http.post(
      formatUrl(apiUrls.createTrap(":sicId", ":unixDate")),
      async ({ request }) => {
        const responseSchema = z.object({
          name: z.string(),
        });

        const data = responseSchema.parse(await request.json());

        // Simulate a server-side auto-incrementing id by getting the current max id and adding 1
        const newAutoIncTrapId = getNextAutoIncValue(
          mockTrapSummaryData.default.map((item) => item.id)
        );
        const title = getTrapNameFromId(newAutoIncTrapId);

        const newTrap: Trap = trapSchema.parse({
          id: newAutoIncTrapId,
          name: data.name,
          title,
          doorId: null,
          notes: null,
          shipmentsTags: [],
          trailerNumber: null,
          shipmentsCount: 0,
          shipmentsPallets: 0,
          shipmentsPieces: 0,
          shipmentsWeight: 0,
          shipmentsWeightUnit: "lbs",
          shipmentIds: [],
          appointmentDate: new Date(2023, 2, 1),
          serviceDueDate: new Date(2023, 2, 1),
        });

        mockTrapSummaryData.default.push(newTrap);

        await delay(config.delay);
        return HttpResponse.json(newTrap, { status: 201 });
      }
    ),

    // * Get Load templates
    http.get(
      formatUrl(apiUrls.getStaticLoadTemplates(":sicCode")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockStaticLoadTemplatesList.default);
      }
    ),

    // * Get Doors
    http.get(formatUrl(apiUrls.getDoorLayouts(":sicCode")), async () => {
      await delay(config.delay);
      return HttpResponse.json(doorLayoutList.default);
    }),

    // * Get preset lane
    http.get(formatUrl(apiUrls.getPresetLanes(":sicCode")), () => {
      delay(config.delay);
      return HttpResponse.json(mockPresetLanesList.default);
    }),

    // * Get preset lane sics suggestion
    http.get(
      formatUrl(
        apiUrls.getPresetLaneSicSuggestion(
          ":sicCode",
          ":destinationSic" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(mockPresetLanesSicsSuggestionList.default);
      }
    ),

    // * Get Outbound linehaul Trailers
    http.get(
      formatUrl(
        apiUrls.getOutboundLinehaulTrailers({
          sicCode: ":sicCode",
          eta: ":eta",
        })
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(linehaulTrailersResponse.default);
      }
    ),

    // * Get Outbound linehaul Trips
    http.get(
      formatUrl(apiUrls.getLinehaulTrips(":sicCode", ":destinationSicCode")),
      () => {
        delay(config.delay);
        const scenario = getUrlParam("disabledTrips");
        if (scenario === "true") {
          return HttpResponse.json(linehaulTripsResponse.empty);
        }
        return HttpResponse.json(linehaulTripsResponse.default);
      }
    ),

    http.get(
      formatUrl(apiUrls.getOutboundLoadTrailers(":sicCode")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(loadTrailersResponse.default);
      }
    ),

    // * Change trailer visibility
    http.put(
      formatUrl(apiUrls.changeTrailerVisibility(":sicCode")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // * Swing Trailer
    http.post(
      formatUrl(
        apiUrls.swingTrailer(":sicCode", ":plannedPullId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // * Get Outbound pnd Trailers
    http.get(formatUrl(apiUrls.getPndTrailers(":sicCode")), () => {
      delay(config.delay);
      return HttpResponse.json(pndTrailersResponse.default);
    }),

    // * Get Outbound trap Trailers
    http.get(formatUrl(apiUrls.getTrapTrailers(":sicCode")), () => {
      delay(config.delay);
      return HttpResponse.json(trapTrailersResponse.default);
    }),

    // * Get Outbound carriers
    http.get(formatUrl(apiUrls.getOutboundCarriers), () => {
      delay(config.delay);
      return HttpResponse.json(carriersList.default);
    }),

    // * Get Outbound equipment
    http.get(formatUrl(apiUrls.getEquipments(":sicCode")), () => {
      delay(config.delay);
      return HttpResponse.json(equipmentList.default);
    }),

    // * Get Outbound Lanes
    http.get(formatUrl(apiUrls.getLanes(":sicCode")), () => {
      delay(config.delay);
      return HttpResponse.json(mockLanesList.default);
    }),

    // * Get Outbound laneDetails
    http.get(formatUrl(apiUrls.getLaneDetailsTonnage(":sicCode", [])), () => {
      delay(config.delay);
      if (config.scenario === "error") {
        return HttpResponse.json(
          {
            success: false,
            response: {
              errorMessage: "Plan not started today",
            },
          },
          {
            status: 400,
          }
        );
      } else {
        return HttpResponse.json(laneDetailsTonnage.default);
      }
    }),

    http.get(
      formatUrl(
        apiUrls.getLaneDetails(":sicCode", ":laneId" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(laneDetails.default);
      }
    ),

    // Get OSAD lane sumary and details
    http.get(
      formatUrl(apiUrls.osad.getOsadDetails(":sicId" as unknown as number)),
      () => {
        delay(config.delay);
        return HttpResponse.json(osadSummaryAndShipments.default);
      }
    ),

    // * Outbound add door and assets
    http.put(
      formatUrl(
        apiUrls.editDoorAndAssetsFromLaneOld(
          ":sicCode",
          ":doorNumber" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.editDoorAndAssetsFromLane(
          ":sicCode",
          ":doorNumber" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // * Outbound assign assets
    http.put(
      formatUrl(
        apiUrls.assignAsset(":sicCode", ":doorNumber" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),
    http.put(
      formatUrl(
        apiUrls.editAssignAsset(
          ":sicCode",
          ":plannedPullId" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),
    // * Outbound assign traps
    http.put(
      formatUrl(
        apiUrls.assignTrap(":sicCode", ":doorNumber" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    // * Outbound edit assign traps
    http.put(
      formatUrl(
        apiUrls.editAssignTrap(
          ":sicCode",
          ":plannedPullId" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    // * Get Outbound linehaul Trailer shipments
    http.get(
      formatUrl(
        apiUrls.getOutboundLinehaulTrailerShipments(
          ":sicCode",
          ":tripNumber" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(linehaulTrailerShipmentsResponse.default);
      }
    ),

    // * Get Outbound loads Trailer shipments
    http.get(
      formatUrl(
        apiUrls.getOutboundLoadTrailerShipments(
          ":sicCode",
          ":plannedPullId" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(loadTrailerShipmentsResponse.default);
      }
    ),

    // * Change status
    http.put(
      formatUrl(
        apiUrls.changeStatusOutboundLoads(
          ":sicCode",
          ":plannedPullId" as unknown as number,
          ":status"
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // * Get Outbound Shipments search
    http.get(
      formatUrl(
        apiUrls.getOutboundShipmentsSearch(
          ":sicCode",
          ":plannedPullId" as unknown as number,
          ":proNumber"
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(shipmentSearchResponse.default);
      }
    ),

    // * Get Door Shipments
    http.get(
      formatUrl(
        apiUrls.getDoorShipments(":sicCode", ":doorNumber" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(doorShipmentsResponse.default);
      }
    ),

    // * Get Lane shipments
    http.get(
      formatUrl(
        apiUrls.getDestinationShipmentsByLane(
          ":sicCode",
          ":destinationType" as unknown as number,
          ":loadToSicId" as unknown as number,
          ":sicId" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        if (params.sicId) {
          let responseData;
          switch (Number(params.sicId)) {
            case 7:
              responseData = dfwLaneShipments.default;
              break;
            case 114:
              responseData = dalLaneShipments.default;
              break;
            default:
              responseData = laneShipments.default;
              break;
          }
          return HttpResponse.json(responseData);
        } else {
          return HttpResponse.json(laneShipments.default);
        }
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getOutboundShipmentDetailsV2(
          ":sicCode",
          ":laneType" as unknown as LaneType,
          ":destinationSicId" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(shipmentDetails.default);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getOutboundShipmentDetails(
          ":sicCode" as unknown as string,
          ":shipment" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(shipmentDetailsHistory.default);
      }
    ),

    http.get(
      formatUrl(apiUrls.getTrapDetails(":sicCode" as unknown as string)),
      () => {
        delay(config.delay);
        return HttpResponse.json(trapDetails.default);
      }
    ),

    http.put(
      formatUrl(
        apiUrls.moveShipmentsToDoor(
          ":sicCode" as unknown as string,
          ":doorNumber" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.moveShipmentsToTrap(
          ":sicCode" as unknown as string,
          ":trapId" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.splitLane(":sicCode", ":destinationSicId" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    // * Get Door Assets
    http.get(
      formatUrl(
        apiUrls.getDoorAssets(
          ":sicCode",
          ":laneType" as unknown as LaneType,
          ":destinationSicId" as unknown as number
        )
      ),
      ({ params }) => {
        if (config.scenario === "unassigned") {
          return HttpResponse.json(doorAssets.unassigned);
        } else if (params.destinationSicId === "10") {
          return HttpResponse.json(doorAssets.bwi);
        } else {
          return HttpResponse.json(doorAssets.default);
        }
      }
    ),

    // * Get Doors Usage Info
    http.get(formatUrl(apiUrls.getDoorsUsageInfo(":sicCode")), () => {
      delay(config.delay);
      return HttpResponse.json(doorsUsageInfo);
    }),

    // * Get Outbound Load Trailer Notes
    http.get(
      formatUrl(
        apiUrls.getLoadTrailerNotes(
          ":sicCode",
          ":plannedPullId" as unknown as number
        )
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(loadTrailerNotesResponse.default);
      }
    ),

    // * Post Outbound Merge Doors
    http.post(
      formatUrl(
        apiUrls.mergeDoors(":sicCode", ":doorNumber" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.mergeDoorsAssets(":sicCode", ":doorNumber" as unknown as number)
      ),
      () => {
        delay(config.delay);
        return HttpResponse.json(null, { status: 201 });
      }
    ),

    // * Post Transform PnD to Trap
    http.post(formatUrl(apiUrls.transformPndToTrap(":sicCode")), () => {
      delay(config.delay);

      return HttpResponse.json(null, { status: 201 });
    }),

    // * Get Outbound Shipment Tags
    http.get(formatUrl(apiUrls.shipmentTags), () => {
      delay(config.delay);
      return HttpResponse.json(shipmentTagsResponse.default);
    }),

    http.post(
      formatUrl(
        apiUrls.createLoadTrailerNote(
          ":sicCode",
          ":plannedPullId" as unknown as number
        )
      ),
      async ({ request }) => {
        const requestSchema = z.object({
          content: z.string(),
        });

        const data = requestSchema.parse(await request.json());
        const newAutoIncNoteId = getNextAutoIncValue(
          loadTrailerNotesResponse.default.map((item) => item.id)
        );

        const newNote: Note = {
          id: newAutoIncNoteId,
          content: data.content,
          userName: "Current User",
          timeStamp: new Date(),
          isEdited: false,
          canEdit: true,
        };

        loadTrailerNotesResponse.default.push(newNote);

        await delay(config.delay);

        return HttpResponse.json(newNote, { status: 201 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.updateLoadTrailerNote(
          ":sicCode",
          ":plannedPullId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.deleteLoadTrailerNote(
          ":sicCode",
          ":plannedPullId" as unknown as number,
          ":noteId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    // * Get loadboard schedule pairs
    http.get(
      formatUrl(apiUrls.loadBoard.getSchedulePair(":pairId")),
      async ({ params }) => {
        await delay(config.delay);
        const { pairId } = params;
        if (!pairId) throw new Error("Missing pairId parameter");
        return HttpResponse.json(mockLoadBoardSchedulePair.multipleLoad);
      }
    ),

    // * Get loadboard change requests
    http.get(
      formatUrl(apiUrls.loadBoard.getChangeRequestLogs(":scheduleId")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockChangeRequestLogs.default);
      }
    ),

    /** Inbound Planning */
    // * Put Delivery Route Status
    http.put(
      formatUrl(apiUrls.putDeliveryRouteStatusOld(":id" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 204 });
      }
    ),
    // PUT Massage Route
    http.put(
      formatUrl(apiUrls.putMassageRoute(":id" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 204 });
      }
    ),

    // * Reassign Shipment
    http.post(formatUrl(apiUrls.reassignShipments), async () => {
      await delay(config.delay);
      return HttpResponse.text(null, { status: 204 });
    }),

    // * Reorder Shipment
    http.patch(
      formatUrl(apiUrls.reorderShipments(":routeId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 204 });
      }
    ),

    // LiveInventory

    http.get(
      formatUrl(apiUrls.getLiveInventory(":sicId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(centralizedEquipmentBySicMock.default);
      }
    ),

    http.get(
      formatUrl(apiUrls.searchEquipmentByRefNumber(":refNumber")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockSearchByRefData.default);
      }
    ),

    http.put(
      formatUrl(apiUrls.repositionTrailer(":equipmentId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true });
      }
    ),

    // * Get Unassigned Shipments
    http.get(
      formatUrl(
        apiUrls.getUnassignedShipments(
          ":sicId" as unknown as number,
          ":date" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockUnassignedShipmentIds);
      }
    ),

    // * Get Route Assigments
    http.get(
      formatUrl(
        apiUrls.getRouteAssignments(
          ":date" as unknown as number,
          ":sicId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockPlannedRoutesTraps.default);
      }
    ),

    http.get(
      formatUrl(apiUrls.getDeliveryRoutesTrapsStatusOld([], [])), // passing empty arrays since the search params are ignored by msw's matching algorithm, and thus, stripped by formatUrl anyway.
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockOldPlannedRouteTrapStatuses.default);
      }
    ),
    http.get(
      formatUrl(apiUrls.getDeliveryRoutesTrapsStatusV3([], [])),
      async () => {
        await delay(config.delay);
        const scenario = getUrlParam("deliveryRoutesTrapsStatusScenario");
        if (scenario === "allTransitionsAllowed") {
          return HttpResponse.json(
            mockPlannedRouteTrapStatuses.allTransitionsAllowed
          );
        }
        return HttpResponse.json(mockPlannedRouteTrapStatuses.default);
      }
    ),

    http.put(formatUrl(apiUrls.putDeliveryRoutesTrapsStatus), async () => {
      await delay(config.delay);
      return HttpResponse.text(null, { status: 204 });
    }),
    http.put<any, { statusId: number; routeIds: number[]; trapIds: number[] }>(
      formatUrl(apiUrls.putDeliveryRoutesTrapsStatusV2),
      async ({ request }) => {
        await delay(config.delay);

        const routesAndTrapsUpdateRequest = await request.json();

        if (routesAndTrapsUpdateRequest.routeIds.includes(312314)) {
          return HttpResponse.json(mockPlannedUpdateRouteTrapStatuses.partial, {
            status: 200,
          });
        }
        return HttpResponse.json(mockPlannedUpdateRouteTrapStatuses.default, {
          status: 200,
        });
      }
    ),

    // * Get Preset Routes
    http.get(formatUrl(apiUrls.getRoutesAndTraps), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const sicId = url.searchParams.get("sicId");
      if (!sicId) throw new Error("Missing sicId parameter");
      return HttpResponse.json(oldMockPresetRoutesAndTraps.default);
    }),

    http.get(formatUrl(apiUrls.getRoutesAndTrapsV2), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const sicId = url.searchParams.get("sicId");
      if (!sicId) throw new Error("Missing sicId parameter");
      return HttpResponse.json(mockPresetRoutesAndTraps.default);
    }),
    // * Put Preset Routes
    http.put(
      formatUrl(apiUrls.putRoutes(":routeId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    // * Get Planning Shipment Details
    http.post<any, number[]>(
      formatUrl(apiUrls.getShipmentDetails),
      async ({ request }) => {
        const shipmentIds = await request.json();
        await delay(config.delay);

        if (
          JSON.stringify(mockShipmentIds.sort()) ===
          JSON.stringify(shipmentIds.sort())
        ) {
          return HttpResponse.json(mockPlannedShipmentsDetails.default);
        }

        if (
          JSON.stringify(mockIncompleteShipmentIds[1]) ===
          JSON.stringify(shipmentIds[1])
        ) {
          return HttpResponse.json(mockIncompleteShipmentsDetails.default);
        }

        const allShipments = [
          ...mockShipmentData.default,
          ...mockPlannedShipmentsDetails.default,
          ...mockIncompleteShipmentsDetails.default,
        ];

        return HttpResponse.json(
          allShipments.filter((s) => shipmentIds.includes(s.id))
        );
      }
    ),

    http.get(
      formatUrl(apiUrls.getShipmentsByRoute(":trailerId")),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const routeId = url.searchParams.get("trailerId");
        if (routeId) {
          const route = mockPlannedRoutesTraps.default.find(
            (r) => r.id === parseInt(routeId, 10)
          );
          const shipmentIds = route ? route.shipmentIds : [];
          const shipments = mockPlannedShipmentsDetails.default.filter(
            (shipment) => shipmentIds.includes(shipment.id)
          );
          return HttpResponse.json(shipments);
        } else {
          return HttpResponse.json(null, { status: 404 });
        }
      }
    ),

    http.get(
      formatUrl(apiUrls.getShipmentsByTrap(":trailerId")),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const trapId = url.searchParams.get("trailerId");
        if (trapId) {
          const route = mockPlannedRoutesTraps.default.find(
            (r) => r.id === parseInt(trapId, 10)
          );
          const shipmentIds = route ? route.shipmentIds : [];
          const shipments = mockPlannedShipmentsDetails.default.filter(
            (shipment) => shipmentIds.includes(shipment.id)
          );
          return HttpResponse.json(shipments);
        } else {
          return HttpResponse.json(null, { status: 404 });
        }
      }
    ),

    // * Get Planned Route statuses
    http.get(
      formatUrl(apiUrls.getDeliveryRouteStatusOld(":id" as unknown as number)),
      async () => {
        await delay(config.delay);
        const scenario = getUrlParam("deliveryRouteStatusScenario");
        if (scenario === "allTransitionsAllowed") {
          return HttpResponse.json(mockRouteStatuses.allTransitionsAllowed);
        }
        return HttpResponse.json(mockRouteStatuses.default);
      }
    ),

    //* Get Planned Trap statuses
    http.get(
      formatUrl(apiUrls.getDeliveryTrapStatusOld(":id" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockTrapStatuses.default);
      }
    ),

    /** Inbound Unplanned Freight */
    // * Get linehaul trips
    http.get(formatUrl(apiUrls.getTripLH), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const searchTerm = url.searchParams.get("searchTerm");
      const destinationSicId = url.searchParams.get("destinationSicId");
      const planDate = url.searchParams.get("planDate");
      if (!destinationSicId) {
        throw new Error("Missing destinationSicId parameter");
      }
      if (!planDate) throw new Error("Missing planDate parameter");
      return HttpResponse.json(
        filterArrayBySearchTerm(
          mockLinehaulTrips.default,
          ["tripNumber", "trailerNumber"],
          searchTerm || ""
        )
      );
    }),

    http.post(
      formatUrl(apiUrls.arriveTrip(":tripNumber" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // * Get linehaul stops
    http.get(formatUrl(apiUrls.getTripLHStops), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const destinationSicId = url.searchParams.get("destinationSicId");
      const planDate = url.searchParams.get("planDate");
      const searchTerm = url.searchParams.get("searchTerm");
      if (!destinationSicId) {
        throw new Error("Missing destinationSicId parameter");
      }
      if (!planDate) throw new Error("Missing planDate parameter");
      return HttpResponse.json(
        filterArrayBySearchTerm(
          mockStops.default,
          ["tripNumber", "trailerNumber"],
          searchTerm || ""
        )
      );
    }),

    // * Get freights
    http.get(formatUrl(apiUrls.getAdditionalFreight), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const searchTerm = url.searchParams.get("searchTerm");
      const destinationSicId = url.searchParams.get("destinationSicId");
      const planDate = url.searchParams.get("planDate");
      if (!destinationSicId) {
        throw new Error("Missing destinationSicId parameter");
      }
      if (!planDate) throw new Error("Missing planDate parameter");
      return HttpResponse.json(
        filterArrayBySearchTerm(
          mockFodShipments.default,
          ["proNumber"],
          searchTerm || ""
        )
      );
    }),

    // * Get shuttle trips
    http.get(formatUrl(apiUrls.getTripShuttle), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const searchTerm = url.searchParams.get("searchTerm");
      const destinationSicId = url.searchParams.get("destinationSicId");
      const planDate = url.searchParams.get("planDate");
      if (!destinationSicId) {
        throw new Error("Missing destinationSicId parameter");
      }
      if (!planDate) throw new Error("Missing planDate parameter");
      return HttpResponse.json(
        filterArrayBySearchTerm(
          mockShuttleTrips.default,
          ["tripNumber", "trailerNumber"],
          searchTerm || ""
        )
      );
    }),

    // * Get Incomplete Trailers
    http.get(
      formatUrl(
        apiUrls.getIncompleteTrailer(
          ":date" as unknown as number,
          ":sicId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockIncompleteTrailers);
      }
    ),

    http.post(
      formatUrl(
        apiUrls.reinstateToPlan(
          ":date" as unknown as number,
          ":sicId" as unknown as number
        )
      ),

      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, {
          status: 200,
        });
      }
    ),

    // * Add to plan
    http.post<any, { shipmentIds: number[] }>(
      formatUrl(
        apiUrls.addToPlan(
          ":sicId" as unknown as number,
          ":date" as unknown as number
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const { shipmentIds } = await request.json();
        const data: AddToPlanResponse = {
          successfullyProcessedShipmentCount: shipmentIds.length,
          failedProcessedShipmentCount: 0,
          failedShipmentIds: [],
          failedShipments: [],
          processedShipmentCount: shipmentIds.length,
          processedShipmentIds: shipmentIds,
          shipmentCountSubmitted: shipmentIds.length,
          shipmentIdsWithMissingConsigneeInfo: [],
          unprocessedShipmentCount: 0,
          unprocessedShipmentSearchResults: [],
        };
        return HttpResponse.json(data);
      }
    ),

    // * Put Preset Routes
    http.put(
      formatUrl(apiUrls.putRoutes(":routeId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getDoorNumbersBySicIdOld(":sicId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(serviceCenterDoorNumbers);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEquipmentDetailsBySicId(":sicId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockEquipmentData.default);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEquipmentById(":equipmentIdParam" as unknown as number)
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { equipmentIdParam } = params;
        const equipmentId = Number(equipmentIdParam);

        const mockEquipments = mockEquipmentData.getById;
        const mockEquipment = mockEquipments.find(
          (equipment) => equipment.id === equipmentId
        );
        return HttpResponse.json(mockEquipment);
      }
    ),

    http.get(formatUrl(apiUrls.getCurrentUser), async () => {
      await delay(config.delay);
      const mockUser: User = {
        id: "7fd75d50-622e-4258-9027-606eb0f11809",
        firstName: "Mock",
        lastName: "User",
        email: "mock@user.com",
        isActive: true,
        sicId: 111,
        groups: [],
        loginId: null,
      };
      return HttpResponse.json(mockUser);
    }),

    http.get(
      formatUrl(apiUrls.getGroup(":groupId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({
          data: {
            id: 999,
            name: "Mock Group",
            description: "Mock Group Description",
          },
        });
      }
    ),

    http.post(
      formatUrl(apiUrls.autoSequenceRoutes([])),
      async ({ request }) => {
        await delay(config.delay);
        const scenario = getUrlParam("autoSequenceRoutesScenario");
        if (scenario === "error") {
          return new HttpResponse(null, {
            status: 500,
            statusText: "Auto sequence routes error",
          });
        }
        const url = new URL(request.url);
        const routeIds = url.searchParams.getAll("routeIds");
        return HttpResponse.json(routeIds.map(Number));
      }
    ),

    http.get(
      formatUrl(apiUrls.getDeliveryRoutesSequenceStatus(":routeIds")),
      async ({ request }) => {
        // Delay to show Auto Sequence feedback
        await delay(300);
        const url = new URL(request.url);
        const routeIds = url.searchParams.getAll("routeIds");
        const result: DeliveryRouteSequenceStatus[] = routeIds.map(
          (routeId) => ({ id: Number(routeId), sequenceStatus: "processed" })
        );
        return HttpResponse.json(result);
      }
    ),

    http.post(formatUrl(apiUrls.lockRoutes), async () => {
      await delay(config.delay);
      return new HttpResponse(null, { status: 204 });
    }),

    http.get(
      formatUrl(apiUrls.getDoorNumbersBySicId(":sicId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockServiceCenterDoors.default);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEquipmentsBySicIdAndTypeId(
          ":sicIdParam" as unknown as number,
          ":typeParam" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { typeParam } = params;
        if (!typeParam) {
          return HttpResponse.json([]);
        }

        const allowedEquipmentTypes = Object.values(equipmentTypeMap);
        if (
          !allowedEquipmentTypes.includes(Number(typeParam) as EquipmentTypeId)
        ) {
          throw new Error("Invalid equipment type");
        }

        const equipmentType = Object.keys(equipmentTypeMap).find(
          (key) =>
            equipmentTypeMap[key as keyof typeof equipmentTypeMap] ===
            Number(typeParam)
        ) as EquipmentType | undefined;

        if (equipmentType && equipmentType in equipmentTypeMap) {
          return HttpResponse.json(
            mockEquipmentsBySicIdAndTypeData.default[equipmentType]
          );
        } else {
          return new HttpResponse(null, {
            status: 400,
            statusText: "Invalid equipment type",
          });
        }
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEquipmentsUsageBySicId(":sicIdParam" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockEquipmentsUsageBySicIdData.default, {
          status: 200,
        });
      }
    ),

    http.get(
      formatUrl(apiUrls.getEquipmentBySic(":sicId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(centralizedEquipmentBySicMock.default);
      }
    ),

    http.put(
      formatUrl(apiUrls.putRouteAssignment(":routeId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockRouteAssignment.default, { status: 200 });
      }
    ),

    // * Drivers

    http.get(
      formatUrl(apiUrls.getDrivers(":sicId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDrivers.default);
      }
    ),

    http.get(
      formatUrl(apiUrls.getDriverById(":driverId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDrivers.default[0]);
      }
    ),

    http.put(
      formatUrl(apiUrls.putDriver(":driverId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDrivers.default[0]);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getDriversUsageBySicIdAndPlanDate(
          ":sicIdParam" as unknown as number,
          ":planDateParam" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDriversUsage.default, {
          status: 200,
        });
      }
    ),

    // * Reports
    http.get(
      formatUrl(apiUrls.getOutboundLinehaulTrailerShipmentsPdf(":sicCode", [])),
      getMockReport
    ),
    http.get(
      formatUrl(apiUrls.getOutboundPndTrailerShipmentsPdf(":sicCode", [])),
      getMockReport
    ),
    http.get(
      formatUrl(apiUrls.getOutboundTrapTrailerShipmentsPdf(":sicCode", [])),
      getMockReport
    ),
    http.get(
      formatUrl(apiUrls.getOutboundLoadTrailerShipmentsPdf(":sicCode", [])),
      getMockReport
    ),
    http.put(
      formatUrl(apiUrls.getOutboundLinehaulTrailerManifestReport),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockPrintResult.default, {
          status: 200,
        });
      }
    ),
    http.put(
      formatUrl(apiUrls.getOutboundCarrierBillOfLadingManifestReport),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockPrintResult.default, {
          status: 200,
        });
      }
    ),

    http.put(formatUrl(apiUrls.getOutboundTrapManifestReport), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockPrintResult.default, {
        status: 200,
      });
    }),
    http.put(formatUrl(apiUrls.getOutboundStripManifestReport), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockPrintResult.default, {
        status: 200,
      });
    }),
    http.post(formatUrl(apiUrls.getBulkReport), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockPrintResult.default, {
        status: 200,
      });
    }),

    http.get(
      formatUrl(
        apiUrls.getEndOfShiftReports(
          ":originSicId" as unknown as number,
          ":date",
          "report-header"
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(reportHeader, {
          status: 200,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEndOfShiftReports(
          ":originSicId" as unknown as number,
          ":date",
          "yard-check"
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(yardCheck.default);
      }
    ),

    http.post(
      formatUrl(
        apiUrls.getEndOfShiftReports(
          ":sicId" as unknown as number,
          ":date",
          "yard-check"
        )
      ),
      async ({ request }) => {
        await delay(config.delay);

        try {
          const formData = await request.formData();
          const file = formData.get("file") as File;

          // Check file size (5MB limit)
          if (file.size > 5 * 1024 * 1024) {
            return HttpResponse.json(yardCheckFileUpload.errors.fileTooLarge, {
              status: 413,
            });
          }

          // Check file type
          const allowedExtensions = [".pdf", ".doc", ".docx", ".xlsx", ".csv"];
          const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

          if (!allowedExtensions.includes(fileExtension)) {
            return HttpResponse.json(
              yardCheckFileUpload.errors.invalidFileType,
              {
                status: 400,
              }
            );
          }

          return HttpResponse.json(yardCheckFileUpload.success, {
            status: 200,
          });
        } catch (error) {
          return HttpResponse.json(yardCheckFileUpload.errors.serverError, {
            status: 500,
          });
        }
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.deleteYardCheckFile(":sicId" as unknown as number, ":fileId")
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 200 });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.getEndOfShiftReports(
          ":originSicId" as unknown as number,
          ":date",
          "lane-projections"
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(laneProjections, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.getEndOfShiftReportDownload(
          ":sicId" as unknown as number,
          ":fileUri"
        )
      ),
      async () => {
        await delay(config.delay);

        // Return a mock pre-signed URL response
        return HttpResponse.json(
          {
            url: "https://mock-s3-url.com/documents/mock-file.pdf?X-Amz-Expires=900&X-Amz-Security-Token=mock-token",
          },
          {
            status: 200,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }
    ),
    /** Inbound Urgent and Critical Care */
    http.get(
      formatUrl(
        apiUrls.getInboundUrgentAndCriticalCare(
          ":sicCode" as unknown as number,
          ":planDate" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockInboundUrgentAndCriticalCare);
      }
    ),

    /** UC/CC OFD Shipment Details */
    http.get(
      formatUrl(
        apiUrls.getUcccOfdShipmentDetails(":shipmentId" as unknown as string)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockUcccOfdShipmentDetails.default.shipment);
      }
    ),

    // * Dispatch Routes
    http.get(
      formatUrl(
        apiUrls.dispatch.getLatestDriverLocations(
          ":sicCode" as unknown as number,
          ":planDate" as unknown as string
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDriverLatestLocations.default, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getRoutes(":sicCode" as unknown as number, ":planDate")
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDispatchRoutes.default, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(apiUrls.dispatch.getRoute(":id" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const { id } = params;
        const routeId = Number(id);
        if (!routeId) {
          return new HttpResponse(null, { status: 404 });
        }
        const route = mockDispatchRouteDetail.default.find(
          (route) => route.id === routeId
        );
        if (!route) {
          return new HttpResponse(null, { status: 404 });
        }
        return HttpResponse.json(route, { status: 200 });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getSummary(
          ":sicId" as unknown as number,
          ":planDate" as unknown as string
        )
      ),
      async () => {
        await delay(config.delay);
        const scenario = getUrlParam("planDate");
        type scenarios = keyof typeof mockSummary;
        if (scenario) {
          if (!Object.keys(mockSummary).includes(scenario)) {
            throw new Error("Invalid summary scenario");
          }
          return HttpResponse.json(mockSummary[scenario as scenarios], {
            status: 200,
          });
        }
        return HttpResponse.json(mockSummary.default, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getStops(
          ":sicId" as unknown as number,
          ":planDate" as unknown as string
        )
      ),
      async () => {
        await delay(config.delay);
        const scenario = getUrlParam("stopsScenario");
        type scenarios = keyof typeof mockDispatchStops;
        // Stop 18 with only one matching route (exclude stop 20) for merge/single-route tests
        if (scenario === "singleMatchingRoute") {
          const stops = mockDispatchStops.default
            .filter((stop) => stop.id !== 20)
            .map((stop) => {
              const {
                pickups: _pickups,
                deliveries: _deliveries,
                ...stopWithoutArrays
              } = stop;
              return stopWithoutArrays;
            });
          return HttpResponse.json(stops, { status: 200 });
        }
        if (scenario) {
          if (!Object.keys(mockDispatchStops).includes(scenario)) {
            throw new Error("Invalid stops scenario");
          }
          // Transform stops to have empty arrays (API no longer returns shipment arrays)
          // Mock data includes arrays for StopDetails, but /stops endpoint removes them
          const stops = mockDispatchStops[scenario as scenarios].map((stop) => {
            const {
              pickups: _pickups,
              deliveries: _deliveries,
              ...stopWithoutArrays
            } = stop;
            return stopWithoutArrays;
          });
          return HttpResponse.json(stops, {
            status: 200,
          });
        }

        // Transform stops to have empty arrays (API no longer returns shipment arrays)
        // Mock data includes arrays for StopDetails, but /stops endpoint removes them
        const stops = mockDispatchStops.default.map((stop) => {
          const {
            pickups: _pickups,
            deliveries: _deliveries,
            ...stopWithoutArrays
          } = stop;
          return stopWithoutArrays;
        });
        return HttpResponse.json(stops, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getStop(
          ":sicId" as unknown as number,
          ":id" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const stop = mockDispatchStops.default.find(
          (stop) => stop.id === Number(params.id)
        );

        if (!stop) {
          return new HttpResponse(null, { status: 404 });
        }

        // Return stop with full shipment arrays for StopDetails endpoint
        return HttpResponse.json(stop, {
          status: 200,
        });
      }
    ),
    http.post(
      formatUrl(
        apiUrls.dispatch.postUnassignStopFromRoute(
          ":sicId" as unknown as number,
          ":id" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const scenario = getUrlParam("unassignStopScenario");
        if (scenario === "error") {
          return new HttpResponse(null, {
            status: 500,
          });
        }
        const stop = mockDispatchStops.default.find(
          (stop) => stop.id === Number(params.id)
        );
        if (!stop) {
          return new HttpResponse(null, { status: 404 });
        }

        return new HttpResponse(null, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getSuggestedRoutes(
          ":sicCode" as unknown as number,
          ":stopId" as unknown as number,
          ":planDate" as unknown as string
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDispatchSuggestedRoutes.default, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getSuggestedStops(
          ":sicId" as unknown as number,
          ":routeId" as unknown as number,
          ":planDate" as unknown as string
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockDispatchSuggestedStops.default, {
          status: 200,
        });
      }
    ),
    http.post(
      formatUrl(
        apiUrls.dispatch.postAssignStopToRoute(
          ":sicCode" as unknown as number,
          ":stopId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        const assignResponse: AssignStopResponse = {
          routeName: "TEST ROUTE",
          driverName: "TEST DRIVER",
        };
        return HttpResponse.json(assignResponse, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.dispatch.postMergeStop(
          ":sicCode" as unknown as number,
          ":stopId" as unknown as number
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as { unassignedStopId: number };
        if (!body.unassignedStopId) {
          return HttpResponse.json(
            { title: "Unassigned stop is required." },
            { status: 400 }
          );
        }
        const mergeResponse: AssignStopResponse = {
          routeName: "TEST ROUTE",
          driverName: "TEST DRIVER",
        };
        return HttpResponse.json(mergeResponse, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.dispatch.postMultiAssignStopsToRoute(
          ":sicCode" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.dispatch.postReassignStopToRoute(
          ":sicCode" as unknown as number,
          ":stopId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.dispatch.postSplitStop(
          ":sicCode" as unknown as number,
          ":stopId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 200 });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getStopShipmentNotes(
          ":sicId" as unknown as number,
          ":id" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { id } = params;
        const shipmentId = Number(id);
        const scenario = getUrlParam("stopShipmentScenario");

        const size = mockDispatchStopShipmentNotes.default.length + 1;
        const index = shipmentId % size;

        const mockDispatchStopShipmentNote =
          mockDispatchStopShipmentNotes.default[index];

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (scenario === "noShipmentNotes" || !mockDispatchStopShipmentNote) {
          return new HttpResponse(null, { status: 204 });
        }

        return HttpResponse.json(mockDispatchStopShipmentNote, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getStopSuggestedSics(
          ":sicId" as unknown as number,
          ":id" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        return HttpResponse.json(
          mockDispatchStopSuggestedSics.default.filter(
            (x) => x.sicId !== Number(params.sicId)
          ),
          {
            status: 200,
          }
        );
      }
    ),
    http.patch(
      formatUrl(
        apiUrls.dispatch.patchStopSic(
          ":sicCode" as unknown as number,
          ":id" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return new HttpResponse(null, { status: 200 });
      }
    ),
    http.post(
      formatUrl(
        apiUrls.dispatch.postUpdateRouteStatus(
          ":sicId" as unknown as number,
          ":id" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const scenario = getUrlParam("updateRouteStatusScenario");
        if (scenario === "error") {
          return new HttpResponse(null, {
            status: 500,
          });
        }
        const stop = mockDispatchStops.default.find(
          (stop) => stop.id === Number(params.id)
        );
        if (!stop) {
          return new HttpResponse(null, { status: 404 });
        }

        return new HttpResponse(null, {
          status: 200,
        });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getAvailableRouteStatuses(
          ":sicId" as unknown as number,
          ":routeId" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);

        const route = mockDispatchRoutes.default.find(
          (route) => Number(params.routeId) === route.id
        );

        const routeStatuses = ((routeStatus: RouteStatus | undefined) => {
          switch (routeStatus) {
            case "DISP":
              return mockDispatchAvailableRouteStatuses.routeWithStatusDisp;
            case "CLDV":
              return mockDispatchAvailableRouteStatuses.routeWithStatusCldv;
            case "CLDK":
              return mockDispatchAvailableRouteStatuses.routeWithStatusCldk;
            default:
              return null;
          }
        })(route?.status);

        if (!routeStatuses) {
          return new HttpResponse(null, { status: 404 });
        }
        return HttpResponse.json(routeStatuses, {
          status: 200,
        });
      }
    ),
    http.post(
      formatUrl(
        apiUrls.dispatch.postRescheduleStop(
          ":sicCode" as unknown as number,
          ":stopId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        const scenario = getUrlParam("rescheduleStopScenario");
        if (scenario === "error") {
          return new HttpResponse(null, {
            status: 500,
          });
        }
        return new HttpResponse(null, { status: 200 });
      }
    ),
    http.get(
      formatUrl(
        apiUrls.dispatch.getNotifications(
          ":sicCode" as unknown as number,
          ":planDate"
        )
      ),
      async () => {
        await delay(config.delay);
        const scenario = getUrlParam("notificationsScenario");
        if (scenario === "read") {
          return HttpResponse.json(
            mockNotifications.default.map((notification) => ({
              ...notification,
              isRead: true,
            })),
            {
              status: 200,
            }
          );
        }

        const notifications = mockNotifications.default.map((notification) => ({
          ...notification,
          isRead:
            readNotificationIds.has(notification.id) || notification.isRead,
        }));

        return HttpResponse.json(notifications, {
          status: 200,
        });
      }
    ),

    http.post(
      formatUrl(apiUrls.dispatch.postMarkNotificationsAsRead()),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as { notificationIds: number[] };

        body.notificationIds.forEach((id) => {
          readNotificationIds.add(id);
        });

        return HttpResponse.json(null, {
          status: 200,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getShipments(
          ":sicId" as unknown as number,
          ":planDate" as unknown as string,
          ":searchTerm" as unknown as string | null | undefined
        )
      ),
      async () => {
        await delay(config.delay);

        return HttpResponse.json(mockShipmentInsights, {
          status: 200,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getDrivers(
          ":sicId" as unknown as number,
          ":planDate" as unknown as string,
          ":searchTerm" as unknown as string | null | undefined
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const searchTerm = url.searchParams.get("searchTerm");

        let filteredDrivers = mockInsightsDrivers;

        // Apply search term filtering if provided
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          filteredDrivers = mockInsightsDrivers.filter(
            (driver) =>
              driver.driver.toLowerCase().includes(term) ||
              driver.route.toLowerCase().includes(term)
          );
        }

        return HttpResponse.json(filteredDrivers, {
          status: 200,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getShipmentDetails(":shipmentId" as unknown as number)
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { shipmentId } = params;
        const validIds = Object.keys(mockShipmentDetails);

        if (!shipmentId || validIds.indexOf(shipmentId as string) === -1) {
          return new HttpResponse(null, { status: 404 });
        }
        const shipment = mockShipmentDetails[shipmentId as string];
        return HttpResponse.json(shipment, { status: 200 });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getShipmentNotes(":shipmentId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(defaultShipmentNotes, { status: 200 });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getShipmentHistory(":shipmentId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockShipmentHistory, { status: 200 });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.dispatch.getShipmentDocuments(
          ":shipmentId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockShipmentDocuments, { status: 200 });
      }
    ),

    // Loadboard V2 Routes

    http.get(formatUrl(apiUrls.loadBoard.daySummary), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockDaySummary.default, { status: 200 });
    }),

    http.get(formatUrl(apiUrls.loadBoard.daySummaryV2(":date")), () =>
      HttpResponse.json(mockDaySummaryV2.default, { status: 200 })
    ),

    http.get(
      formatUrl(
        apiUrls.loadBoard.serviceCenterShipmentsByDay(":originSicCode", ":date")
      ),
      () => {
        // Get unique shipments by shipmentId
        const shipmentMap = new Map<number, any>();
        for (const shipment of mockDaySummaryV2.default.flatMap(
          (i) => i.shipments
        )) {
          shipmentMap.set(shipment.shipmentId, shipment);
        }
        const shipments = Array.from(shipmentMap.values());
        return HttpResponse.json(shipments, { status: 200 });
      }
    ),

    http.get(formatUrl(apiUrls.getRegionsWithSics), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockRegions.default, { status: 200 });
    }),

    http.get(formatUrl(apiUrls.loadBoard.daySummaryV2(":date")), () =>
      HttpResponse.json(mockLoadAndHoldReport.default, { status: 200 })
    ),

    http.get(
      formatUrl(
        apiUrls.getUrgentCriticalCareReports(
          ":originSicCode" as unknown as number,
          ":date"
        )
      ),
      () => HttpResponse.json(mockUrgentCareReport.default, { status: 200 })
    ),

    http.get(
      formatUrl(
        apiUrls.getVendorComplianceReports(
          ":originSicCode" as unknown as number,
          ":date"
        )
      ),
      () =>
        HttpResponse.json(mockVendorComplianceReport.default, { status: 200 })
    ),

    http.get(
      formatUrl(
        apiUrls.getLoadPlanReportsPdf(
          ":originSicCode" as unknown as number,
          ":action",
          ":date",
          ":reportType",
          ":generatedTimestamp",
          ":timezone"
        )
      ),
      ({ params }) => {
        const { action, reportType } = params;

        const pdfBase64 = `
        JVBERi0xLjQKJeLjz9MKMSAwIG9iago8PC9UeXBlL0NhdGFsb2cvUGFnZXMgMiAwIFIgPj4KZW5kb2Jq
        CjIgMCBvYmoKPDwvVHlwZS9QYWdlcy9LaWRzWzMgMCBSXS9Db3VudCAxPj4KZW5kb2JqCjMgMCBvYmoK
        PDwvVHlwZS9QYWdlL1BhcmVudCAyIDAgUi9NZWRpYUJveFswIDAgNjEyIDc5Ml0vUmVzb3VyY2VzPDwv
        Rm9udDw8L0YwIDQgMCBSPj4+Pi9Db250ZW50cyA1IDAgUj4+CmVuZG9iago0IDAgb2JqCjw8L1R5cGUv
        Rm9udC9TdWJ0eXBlL1R5cGUxL05hbWUvRjAvQmFzZUZvbnQvSGVsdmV0aWNhPj4KZW5kb2JqCjUgMCBv
        YmoKPDwvTGVuZ3RoIDYzPj4Kc3RyZWFtCkJUCi9GMCBUIDI0IFRmCjAgMCBUZAooSGVsbG8gV29ybGQh
        KSBUagpFVAplbmRzdHJlYW0KZW5kb2JqCnhyZWYKMCA2CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAw
        MDAxNSAwMDAwMCBuIAowMDAwMDAwMDY3IDAwMDAwIG4gCjAwMDAwMDAxMjggMDAwMDAgbiAKMDAwMDAw
        MDI0MCAwMDAwMCBuIAowMDAwMDAwMzQ3IDAwMDAwIG4gCnRyYWlsZXIKPDwvUm9vdCAxIDAgUi9TaXpl
        IDY+PgpzdGFydHhyZWYKNDA2CiUlRU9GCg==`.replace(/\s+/g, "");

        // Using atob cause there's no Buffer available into Vite's engine
        // eslint-disable-next-line deprecation/deprecation
        const binaryString = atob(pdfBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: "application/pdf" });

        const currentTimestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const filename = `${reportType}_${currentTimestamp}.pdf`;

        const headers: Record<string, string> = {
          "Content-Type": "application/pdf",
        };

        if (action === "print") {
          headers["Content-Disposition"] = `inline; filename=${filename}`;
        } else {
          // Default to download
          headers["Content-Disposition"] = `attachment; filename=${filename}`;
        }

        return new HttpResponse(blob, {
          status: 200,
          headers,
        });
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getTripsReports(":originSicCode" as unknown as number, ":date")
      ),
      () =>
        HttpResponse.json(mockTripsReport.default, {
          status: 200,
        })
    ),

    http.get(
      formatUrl(
        apiUrls.getLoadAndHoldReports(
          ":originSicCode" as unknown as number,
          ":date"
        )
      ),
      () =>
        HttpResponse.json(mockLoadAndHoldReport.default, {
          status: 200,
        })
    ),

    http.get(formatUrl(apiUrls.getEmployeeDrivers), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockEmployeeDrivers.default, { status: 200 });
    }),

    http.get(formatUrl(apiUrls.loadBoard.tripsForReassign), () =>
      HttpResponse.json(mockTripsForReassign.default, { status: 200 })
    ),

    http.post(formatUrl(apiUrls.loadBoard.reassignShipments), () =>
      HttpResponse.json([12345], { status: 200 })
    ),

    http.post(formatUrl(apiUrls.loadBoard.calculateRollover), () =>
      HttpResponse.json(
        {
          isFeeApplicable: true,
          fee: 250,
          feeType: "Rollover",
        },
        { status: 200 }
      )
    ),

    http.put(
      formatUrl(
        apiUrls.updateLoadPlanReportNotes(
          ":sicCode" as unknown as number,
          ":tripNumber" as unknown as number
        )
      ),
      () => HttpResponse.json(null, { status: 200 })
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.updateSchedule(":sicCode" as unknown as number)
      ),
      () => HttpResponse.json(null, { status: 200 })
    ),

    http.post(formatUrl(apiUrls.loadBoard.createSchedulePair), () =>
      HttpResponse.json(null, { status: 200 })
    ),

    http.post(formatUrl(apiUrls.loadBoard.batchPublishSchedules), () =>
      HttpResponse.json([], { status: 200 })
    ),

    http.get(formatUrl(apiUrls.loadBoard.ucccPlanning(":day")), () =>
      HttpResponse.json(mockUcccPlanningShipments, { status: 200 })
    ),

    http.post(
      formatUrl(
        apiUrls.loadBoard.closeTrip(":tripNumber" as unknown as number)
      ),
      () => HttpResponse.json(null, { status: 200 })
    ),

    http.post(formatUrl(apiUrls.loadBoard.reinstateSchedulePair), () =>
      HttpResponse.json(null, { status: 200 })
    ),

    http.get(
      formatUrl(
        apiUrls.loadBoard.closedTripDetails(":tripNumber" as unknown as number)
      ),
      () => HttpResponse.json(mockedClosedTripDetails.default, { status: 200 })
    ),

    http.get(
      formatUrl(
        apiUrls.loadBoard.tripOptimization(":tripNumber" as unknown as number)
      ),
      () => {
        const scenario = getUrlParam("scenario");

        if (scenario === "withStops") {
          return HttpResponse.json(mockTripOptimization.withStops, {
            status: 200,
          });
        }
        return HttpResponse.json(mockTripOptimization.default, { status: 200 });
      }
    ),

    // Move Shipments in Trip Optimization
    http.patch(
      formatUrl(
        apiUrls.loadBoard.moveShipments(":tripNumber" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // Trip List
    // Linehaul Trips
    http.get(formatUrl(apiUrls.loadBoard.tripList(":day")), () =>
      HttpResponse.json(loadTrips, { status: 200 })
    ),

    // Transit Dashboard - supports pagination for infinite scroll testing
    http.get(
      formatUrl(apiUrls.loadBoard.transitDashboardTripsInTransit),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const parsedLimit = Number(url.searchParams.get("limit"));
        const parsedOffset = Number(url.searchParams.get("offset"));
        const limit =
          Number.isNaN(parsedLimit) || parsedLimit < 1 ? 100 : parsedLimit;
        const offset =
          Number.isNaN(parsedOffset) || parsedOffset < 0 ? 0 : parsedOffset;

        // Generator function for infinite scroll testing
        const mockTrips = mockInTransitTrips.trips;
        const hasTrips = Array.isArray(mockTrips) && mockTrips.length > 0;
        const tripsLength = hasTrips ? mockTrips.length : 1;

        const generateTrip = (index: number) => ({
          tripNumber: 10016841 + index,
          loadBoardScheduleId: 3104048 + index,
          originSic: hasTrips
            ? mockTrips[index % tripsLength]?.originSic ?? "UNK"
            : "UNK",
          destinationSic: hasTrips
            ? mockTrips[index % tripsLength]?.destinationSic ?? "UNK"
            : "UNK",
          stops: index % 3 === 0 ? ["SEFL"] : [],
          deadheadOrigin: "",
          deadheadDestination: "",
          bookingNumber: `BK${100000 + index}`,
          carrier: hasTrips
            ? mockTrips[index % tripsLength]?.carrier ?? "UNKNOWN"
            : "UNKNOWN",
          trailer: `TRL-${index.toString().padStart(3, "0")}`,
          shipmentTags: ["CC", "UC"],
          pickupDate: new Date("2025-11-03T23:25:00"),
          eta: new Date("2025-11-07T18:00:00"),
          ata: null,
          tripStatus: "PLAN",
          isStarred: index % 5 === 0,
          isNote: index % 4 === 0,
          employeeDrivers: [{ name: `DRIVER ${index}` }],
          ibCuttime: new Date("2025-11-04T16:30:00"),
          category: ["ON TIME", "RISK", "LATE", "AT RISK"][index % 4],
          latestModifiedOn: new Date("2025-11-03T10:22:00"),
        });

        const totalCount = Number.isFinite(mockInTransitTrips.totalCount)
          ? Math.max(0, Math.floor(Number(mockInTransitTrips.totalCount)))
          : Array.isArray(mockInTransitTrips.trips)
            ? mockInTransitTrips.trips.length
            : 0;

        const remainingItems = totalCount - offset;
        const trips =
          offset >= totalCount || remainingItems <= 0
            ? []
            : Array.from({ length: Math.min(limit, remainingItems) }, (_, i) =>
                generateTrip(offset + i)
              );

        return HttpResponse.json({ totalCount, trips }, { status: 200 });
      }
    ),

    // Transit Dashboard - Delivered Trips
    http.get(
      formatUrl(apiUrls.loadBoard.transitDashboardDeliveredTrips),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(
          {
            totalCount: mockDeliveredTrips.totalCount,
            trips: mockDeliveredTrips.default,
          },
          { status: 200 }
        );
      }
    ),

    // Transit Dashboard - Search (supports Planning, InTransit, and Delivered tabs)
    http.get(
      formatUrl(apiUrls.loadBoard.transitDashboardSearch),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const tab = url.searchParams.get("Tab");
        const searchValue = url.searchParams.get("SearchValue");

        // Return appropriate data based on tab
        if (tab === "Delivered") {
          // Filter delivered trips based on search value (simplified mock logic)
          const filteredTrips = searchValue
            ? mockDeliveredTrips.default.filter((trip) =>
                trip.tripNumber.toString().includes(searchValue)
              )
            : mockDeliveredTrips.default;

          return HttpResponse.json(
            {
              totalCount: filteredTrips.length,
              tab: "Delivered",
              deliveredTrips: filteredTrips,
            },
            { status: 200 }
          );
        }

        // Default/fallback for other tabs
        return HttpResponse.json(
          {
            totalCount: 0,
            tab: tab || "Planning",
            trips: [],
          },
          { status: 200 }
        );
      }
    ),

    http.get(formatUrl(apiUrls.getLinehaulTrailers), () =>
      HttpResponse.json(mockEquipment, { status: 200 })
    ),

    http.get(formatUrl(apiUrls.getLinehaulTrailers), () =>
      HttpResponse.json(trailers, { status: 200 })
    ),

    http.get(formatUrl(apiUrls.loadBoard.ucccNotifications(":date")), () =>
      HttpResponse.json(notifications.default)
    ),

    http.get(
      formatUrl(apiUrls.loadBoard.tripDetails(":tripId" as unknown as number)),
      () => {
        const rolloverFeeApplicable = "rollover-fee-applicable";
        const scenario = getUrlParam("tripStatus");
        const rolloverFeeScenario =
          getUrlParam("rolloverFeeScenario") ?? rolloverFeeApplicable;
        const rolloverFeeDetails =
          rolloverFeeScenario === rolloverFeeApplicable
            ? mockRolloverFeeDetails.applicable
            : mockRolloverFeeDetails.notApplicable;
        switch (scenario) {
          case "ENRT":
            return HttpResponse.json({
              ...mockTripDetails.enroute,
              ...rolloverFeeDetails,
            });
          case "CLOS":
            return HttpResponse.json({
              ...mockTripDetails.closed,
              ...rolloverFeeDetails,
            });
          case "UNPUBLISHED":
            return HttpResponse.json({
              ...mockTripDetails.unpublished,
              ...rolloverFeeDetails,
            });
          case "PUBLISHED":
            return HttpResponse.json({
              ...mockTripDetails.published,
              ...rolloverFeeDetails,
            });
          case "NO_GATE_CUT_DATE":
            return HttpResponse.json({
              ...mockTripDetails.published,
              ...rolloverFeeDetails,
              gateCutDate: null,
            });
          case "CANCELLED":
            return HttpResponse.json({
              ...mockTripDetails.cancelled,
              ...rolloverFeeDetails,
            });
          case "FOREIGN_TRAILER":
            return HttpResponse.json({
              ...mockTripDetails.default,
              ...rolloverFeeDetails,
              isDyltTrailer: false,
              scacCode: "",
              trailerNumber: "",
            });
          default:
            return HttpResponse.json({
              ...mockTripDetails.default,
              ...rolloverFeeDetails,
            });
        }
      }
    ),

    http.post(
      formatUrl(apiUrls.loadBoard.enrouteTrip(":tripId" as unknown as number)),
      () => HttpResponse.json(mockTripDetails.default)
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.loadAndHold(":scheduleId" as unknown as number)
      ),
      () => HttpResponse.json(mockTripDetails.loadAndHold)
    ),

    http.get(formatUrl(apiUrls.getCarrierScacCodes), () =>
      HttpResponse.json(mockScacCodes.default)
    ),

    http.get(formatUrl(apiUrls.loadBoard.delayReasons), () =>
      HttpResponse.json(mockDelayReasons.default)
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.editTripClosedDetails(":tripId" as unknown as number)
      ),
      () => HttpResponse.json(mockTripDetails.default)
    ),

    http.patch(
      formatUrl(
        apiUrls.loadBoard.changeTripStatus(":tripId" as unknown as number)
      ),
      () => HttpResponse.json(mockTripDetails.default)
    ),

    http.post(
      formatUrl(
        apiUrls.loadBoard.updateTransitStatus(
          ":tripNumber" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json({ success: true }, { status: 200 });
      }
    ),

    http.get(formatUrl(apiUrls.getSicByCode(":sicCode")), () =>
      HttpResponse.json(mockSicDetails.default)
    ),

    http.post(formatUrl(apiUrls.addSicContact(":sicCode")), () =>
      HttpResponse.json(mockSicDetails.default)
    ),

    http.get(
      formatUrl(
        apiUrls.getContactBySicCodeAndId(
          ":sicCode",
          ":contactId" as unknown as number
        )
      ),
      () => HttpResponse.json(mockSicContact.default)
    ),

    http.delete(
      formatUrl(
        apiUrls.getContactBySicCodeAndId(
          ":sicCode",
          ":contactId" as unknown as number
        )
      ),
      () => HttpResponse.json(mockSicContact.default)
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.updateSchedule(":scheduleId" as unknown as number)
      ),
      () => HttpResponse.json(mockTripDetails.default)
    ),
    /** Settings */
    // Route / traps
    // ZipCode and Shipment tags
    http.get(formatUrl(apiUrls.getAvailableTags(":zipcode")), () =>
      HttpResponse.json(mockAvailableZipCodeWithTags.default, {
        status: 200,
      })
    ),

    http.post(formatUrl(apiUrls.createZipCode), () =>
      HttpResponse.json(mockCreateZipCodeWithTags.default)
    ),

    http.put(formatUrl(apiUrls.moveZipCodesV2), () =>
      HttpResponse.json({ status: 200 })
    ),

    http.put(
      formatUrl(
        apiUrls.updateAndDeleteZipcode(":zipcodeId" as unknown as number)
      ),
      async () => {
        await delay(1200);
        return HttpResponse.json(mockUpdateZipCodeWithTags.default, {
          status: 200,
        });
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.updateAndDeleteZipcode(":zipcodeId" as unknown as number)
      ),
      () => HttpResponse.json({ status: 200 })
    ),

    http.get(
      formatUrl(apiUrls.getServiceCentersForLoadPlanReport(":date")),
      () => {
        const scenario = getUrlParam("scenario");
        if (scenario === "no-data") {
          return HttpResponse.json([]);
        }
        return HttpResponse.json(companyOwnedServiceCenters);
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getEndOfShiftReports(
          ":sicId" as unknown as number,
          ":date",
          "unpublished-bookings"
        )
      ),
      () => HttpResponse.json(unpublishedBookings.default)
    ),

    http.get(
      formatUrl(
        apiUrls.getEndOfShiftReports(
          ":sicId" as unknown as number,
          ":date",
          "cancelled-trips"
        )
      ),
      () => HttpResponse.json(cancelledTrips.default)
    ),
    // end of shift report

    http.get(formatUrl(apiUrls.getEndOfShiftComments), ({ request }) => {
      const url = new URL(request.url);
      const contextDate = url.searchParams.get("contextDate");
      const regionId = url.searchParams.get("regionId");

      // Return comments if they match the date and region
      if (contextDate === "2025-08-20" && regionId === "3") {
        return HttpResponse.json(endOfShiftComments.default);
      }
      return HttpResponse.json([]);
    }),
    http.post(formatUrl(apiUrls.postEndOfShiftComment), () =>
      HttpResponse.json(endOfShiftComments.default[0])
    ),
    http.patch(formatUrl(apiUrls.editEndOfShiftComment(":commentId")), () =>
      HttpResponse.json(endOfShiftComments.default[0])
    ),
    http.delete(formatUrl(apiUrls.deleteEndOfShiftComment(":commentId")), () =>
      HttpResponse.json(undefined)
    ),
    http.patch(formatUrl(apiUrls.resolveEndOfShiftComment(":commentId")), () =>
      HttpResponse.json({ data: endOfShiftComments.default[0] })
    ),
    http.patch(
      formatUrl(apiUrls.unresolveEndOfShiftComment(":commentId")),
      () => HttpResponse.json({ data: endOfShiftComments.default[0] })
    ),
    http.get(
      formatUrl(apiUrls.getEndOfShiftLatestModifications),
      ({ request }) => {
        const url = new URL(request.url);
        const contextDate = url.searchParams.get("contextDate");
        const regionId = url.searchParams.get("regionId");

        // Return mock data if we have both parameters
        if (contextDate && regionId) {
          return HttpResponse.json(latestModifications.default);
        }
        return HttpResponse.json(null);
      }
    ),

    http.get(formatUrl(apiUrls.getCancellationReport), ({ request }) => {
      const url = new URL(request.url);
      const fromLoadingDate = url.searchParams.get("fromLoadingDate");
      const toLoadingDate = url.searchParams.get("toLoadingDate");

      if (fromLoadingDate && toLoadingDate && fromLoadingDate > toLoadingDate) {
        return HttpResponse.text(
          "fromLoadingDate must not be greater than toLoadingDate",
          {
            status: 400,
          }
        );
      }
      return HttpResponse.json(cancellationReportMock.default);
    }),

    http.delete(
      formatUrl(
        apiUrls.loadBoard.unpublishSchedule(":scheduleId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.loadBoard.publishSchedule(":scheduleId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // BackOffice

    http.get(formatUrl(apiUrls.getBillers(":searchTerm")), () =>
      HttpResponse.json({
        success: true,
        message: null,
        errorCode: null,
        item: mockBillers,
      })
    ),

    http.patch(formatUrl(apiUrls.assignBiller), async () => {
      await delay(config.delay);
      return HttpResponse.json({
        success: true,
        message: "Bills assigned successfully",
        errorCode: null,
      });
    }),

    http.get(formatUrl(apiUrls.getBackOfficeAccounts(":searchTerm")), () =>
      HttpResponse.json(mockBackOfficeAccounts.default)
    ),

    http.get(
      formatUrl(apiUrls.getSearchAccountsPreferences(":searchQuery")),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const searchQuery =
          url.searchParams.get("search_query")?.toLowerCase() || "";

        const filteredAccounts = searchQuery
          ? mockBackOfficeAccounts.default.item.filter(
              (account) =>
                account.id.toString().includes(searchQuery) ||
                account.accountNumber.toLowerCase().includes(searchQuery) ||
                account.accountName.toLowerCase().includes(searchQuery)
            )
          : [];

        return HttpResponse.json({
          ...mockBackOfficeAccounts.default,
          item: filteredAccounts,
        });
      }
    ),

    http.get(formatUrl(apiUrls.getRecentAccountsPreferences), async () => {
      await delay(config.delay);
      const recentAccounts = mockBackOfficeAccounts.default.item.slice(0, 5);

      return HttpResponse.json({
        ...mockBackOfficeAccounts.default,
        item: recentAccounts,
      });
    }),

    http.post(
      formatUrl(apiUrls.saveAccountPreferences),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          id: number;
          accountNumber: string;
          accountName: string;
        };

        return HttpResponse.json({
          success: true,
          message: "Account preference saved successfully",
          errorCode: null,
          item: {
            id: body.id,
            accountNumber: body.accountNumber || "",
            accountName: body.accountName || "",
          },
        });
      }
    ),

    http.get(formatUrl(apiUrls.getZipCodes(":zipcode")), () =>
      HttpResponse.json(mockBackOfficeLocations.default)
    ),

    http.get(formatUrl(apiUrls.getCommodityClasses()), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockCommodityClassOptions.default);
    }),

    http.get(formatUrl(apiUrls.getAdditionalServices), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockAdditionalServices.default);
    }),

    http.get(formatUrl(apiUrls.getShippingInstructions), async () => {
      await delay(config.delay);
      return HttpResponse.json({
        success: true,
        message: null,
        errorCode: null,
        item: shippingInstructionOptions.map((title, index) => ({
          id: index + 1,
          title,
        })),
      });
    }),

    // Create Quick Quote
    http.post(formatUrl(apiUrls.createQuickQuote), async () => {
      await delay(config.delay);

      const mockResponse = {
        success: true,
        message: "Quote saved",
        item: {
          standardServiceId: 399,
          guaranteedServiceId: 400,
        },
        errorCode: null,
      };

      return HttpResponse.json(mockResponse, { status: 200 });
    }),

    // Update Quick Quote
    http.put(
      new RegExp(
        formatUrl(apiUrls.updateQuickQuote(0)).replace(
          "guaranteedServiceId=0",
          "guaranteedServiceId=\\d+"
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const guaranteedServiceId = url.searchParams.get("guaranteedServiceId");

        const mockResponse = {
          success: true,
          message: "Quick Quote saved",
          item: {
            standardServiceId: guaranteedServiceId
              ? Number(guaranteedServiceId) - 1
              : 723,
            guaranteedServiceId: guaranteedServiceId
              ? Number(guaranteedServiceId)
              : 724,
          },
          errorCode: null,
        };

        return HttpResponse.json(mockResponse, { status: 200 });
      }
    ),

    // Get Quick Quote Details
    http.get(
      formatUrl(
        apiUrls.getQuickQuoteDetails(
          ":guaranteedServiceId" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { guaranteedServiceId } = params;
        const serviceId = Number(guaranteedServiceId);
        if (!serviceId) {
          return new HttpResponse(null, { status: 404 });
        }

        const mockResponse = {
          success: true,
          message: "Quote details retrieved successfully",
          errorCode: null,
          item: mockQuickQuoteSummaryData,
        };

        return HttpResponse.json(mockResponse, { status: 200 });
      }
    ),

    // Send Quote Email
    http.post(formatUrl(apiUrls.sendQuoteEmail), async ({ request }) => {
      await delay(config.delay);
      const url = new URL(request.url);
      const scenario = url.searchParams.get("scenario");

      // Schema validation (reject clearly invalid requests)
      const requestSchema = z.object({
        toAddresses: z.array(z.string().email()).nonempty(),
        subject: z.string().min(1),
        message: z.string().min(1),
        quoteNumber: z.number(),
      });

      let body;
      try {
        body = await request.json();
        requestSchema.parse(body);
      } catch {
        return HttpResponse.json(
          {
            success: false,
            message: "Invalid request",
            errorCode: "VALIDATION_ERROR",
            item: null,
          },
          { status: 400 }
        );
      }

      if (scenario === "error") {
        return HttpResponse.json(
          {
            success: false,
            message: "Failed to send quote email",
            errorCode: "EMAIL_SEND_FAILED",
            item: null,
          },
          { status: 500 }
        );
      }

      return HttpResponse.json(
        {
          success: true,
          message: "Quote email sent successfully",
          errorCode: null,
          item: {
            messageId:
              "0111019a24e415ae-5edf6456-e216-483f-8acd-da986c1e3eb0-000000",
            status: "sent",
          },
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(apiUrls.loadBoard.getNotes(":scheduleId" as unknown as number)),
      () => HttpResponse.json(mockScheduleNotes.default)
    ),

    http.get(
      formatUrl(apiUrls.loadBoard.bulkManifestShipments(":searchTerm")),
      () => HttpResponse.json(mockBulkManifestShipments.default)
    ),

    http.post(formatUrl(apiUrls.getShipments), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockShipments, { status: 200 });
    }),

    http.post(formatUrl(apiUrls.getShipmentsStatus), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            statusCounts: mockShipmentsStatusCounts,
          },
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getServiceLevelOptions), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockServiceLevelOptions,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getFuelPriceList), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: "Fuel price list retrieved successfully",
          errorCode: null,
          item: {
            fuelPrices: mockFuelPriceListOptions,
          },
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getCriteriaLogicOptions), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockCriteriaLogicOperators,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getCriteriaTableNames), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockTableNameOptions,
        },
        { status: 200 }
      );
    }),

    http.get(formatUrl(apiUrls.getCriteriaGroupLogicOptions), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: mockCriteriaGroupLogicOperators,
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(apiUrls.getCriteriaFieldNames(":tableId" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const { tableId } = params;
        const tableIdNum = Number(tableId);

        // Get field names for the specific table
        const fieldNames = mockFieldNamesByTable[tableIdNum] ?? [];

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: fieldNames,
          },
          { status: 200 }
        );
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getCriteriaLookupTypes(":tableId" as unknown as number)
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { tableId } = params;
        const tableIdNum = Number(tableId);

        // Get lookup types for the specific table
        const lookupTypes = mockLookupTypesByTable[tableIdNum] ?? [];

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: lookupTypes,
          },
          { status: 200 }
        );
      }
    ),

    http.post(formatUrl(apiUrls.getFuelScales), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        page: number;
        pageSize: number;
        search: string;
        sortBy: string;
        sortOrder: string;
        status: string;
      };
      const {
        page = 1,
        pageSize = 10,
        search = "",
        sortBy = "",
        sortOrder = "",
        status = "",
      } = body;

      const filteredScales = mockFuelScales.fuelScales.filter((scale) => {
        if (search) {
          const lowerSearch = search.toLowerCase();
          const matchesSearch =
            scale.code.toLowerCase().includes(lowerSearch) ||
            scale.longDescription.toLowerCase().includes(lowerSearch);
          if (!matchesSearch) return false;
        }
        if (status && scale.status !== status) return false;
        return true;
      });

      if (sortBy) {
        filteredScales.sort((a, b) => {
          const getValue = (scale: typeof a) => {
            switch (sortBy) {
              case "code":
                return scale.code;
              case "longDescription":
                return scale.longDescription;
              case "fuelPrice":
                return scale.fuelPrice;
              case "fuelPriceValue":
                return scale.fuelPriceValue
                  ? parseFloat(scale.fuelPriceValue)
                  : 0;
              case "status":
                return scale.status;
              default:
                return "";
            }
          };
          const aVal = getValue(a);
          const bVal = getValue(b);
          const comparison =
            typeof aVal === "number" && typeof bVal === "number"
              ? aVal - bVal
              : String(aVal).localeCompare(String(bVal));
          return sortOrder === "desc" ? -comparison : comparison;
        });
      }

      const totalRecords = filteredScales.length;
      const startIndex = (page - 1) * pageSize;
      const paginatedScales = filteredScales.slice(
        startIndex,
        startIndex + pageSize
      );

      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            fuelScales: paginatedScales,
            pagination: {
              totalRecords,
              totalPages: Math.ceil(totalRecords / pageSize),
              currentPage: page,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),

    http.post(formatUrl(apiUrls.getBillingQueues), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        page: number;
        pageSize: number;
        search: string;
        sortBy?: string;
        sortOrder?: string;
        billState?: number[];
        regionTimeZone?: string[];
        shipmentTag?: string[];
        biller?: string;
      };
      const { page = 1, pageSize = 10, search = "" } = body;

      let filtered = [...mockBills];

      // Dropdown filters are not integrated yet; only search narrows the list (matches useInfiniteBillingQueue).
      if (search.trim()) {
        const q = search.toLowerCase();
        filtered = filtered.filter(
          (b) =>
            b.proNumber.toLowerCase().includes(q) ||
            b.shipper.toLowerCase().includes(q) ||
            b.consignee.toLowerCase().includes(q)
        );
      }

      const unassigned = filtered.filter(
        (b) => b.billStatus === "Unassigned"
      ).length;
      const completed = filtered.filter(
        (b) => b.billStatus === "Completed"
      ).length;
      const totalRecords = filtered.length;
      const startIndex = (page - 1) * pageSize;
      const items = filtered.slice(startIndex, startIndex + pageSize);

      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            billingQueue: items,
            pagination: {
              totalRecords,
              totalPages: Math.ceil(totalRecords / pageSize),
              currentPage: page,
              pageSize,
            },
            metrics: {
              totalBills: totalRecords,
              unassigned,
              completedBills: completed,
            },
          },
        },
        { status: 200 }
      );
    }),

    http.post(
      formatUrl(apiUrls.getLaneMinimumSchedules),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          search: string;
          status: string | null;
          sortByName: boolean;
        };
        const { search = "", status = null, sortByName = false } = body;

        let filteredSchedules = [...mockLaneMinimumSchedules];

        // Filter by search
        if (search) {
          const lowerSearch = search.toLowerCase();
          filteredSchedules = filteredSchedules.filter(
            (schedule) =>
              schedule.name.toLowerCase().includes(lowerSearch) ||
              (schedule.description ?? "").toLowerCase().includes(lowerSearch)
          );
        }

        // Filter by status
        if (status) {
          filteredSchedules = filteredSchedules.filter(
            (schedule) => schedule.status === status
          );
        }

        // Sort by name if requested
        if (sortByName) {
          filteredSchedules.sort((a, b) => a.name.localeCompare(b.name));
        }

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: {
              laneMinimumSchedules: filteredSchedules,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.post(formatUrl(apiUrls.getLaneMinimums), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        laneMinimumScheduleId: number;
        page: number;
        pageSize: number;
        search: string;
        sortBy: string;
        sortOrder: string;
      };
      const { page = 1, pageSize = 25 } = body;

      const allLanes = [...mockLaneMinimumList];

      // Pagination
      const totalRecords = allLanes.length;
      const totalPages = Math.ceil(totalRecords / pageSize);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedLanes = allLanes.slice(startIndex, endIndex);

      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            laneMinimums: paginatedLanes.map((lane) => ({
              ...lane,
              minimumCharge: String(lane.minimumCharge),
            })),
            pagination: {
              totalRecords,
              totalPages,
              currentPage: page,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),

    http.patch(
      formatUrl(
        apiUrls.updateLaneMinimumScheduleStatus(":id" as unknown as number)
      ),
      async ({ request, params }) => {
        await delay(config.delay);
        const scheduleId = Number(params.id);
        const body = (await request.json()) as { status: string };

        const schedule = mockLaneMinimumSchedules.find(
          (s) => s.id === scheduleId
        );

        if (!schedule) {
          return HttpResponse.json(
            {
              success: false,
              message: "Schedule not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        // Update the schedule status in mock data
        schedule.status = body.status;

        return HttpResponse.json(
          {
            success: true,
            message: "Lane minimum schedule status updated successfully",
            errorCode: null,
            item: {
              id: scheduleId,
              status: body.status,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.get(
      formatUrl(apiUrls.getLaneMinimumScheduleById(":id" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const scheduleId = Number(params.id);

        const schedule = mockLaneMinimumSchedules.find(
          (s) => s.id === scheduleId
        );

        if (!schedule) {
          return HttpResponse.json(
            {
              success: false,
              message: "Schedule not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        // Return mock detail data with updated metadata for the requested schedule
        return HttpResponse.json(
          {
            success: true,
            message: "Schedule retrieved successfully",
            errorCode: null,
            item: {
              ...mockLaneMinimumScheduleDetail,
              id: schedule.id,
              name: schedule.name,
              description: schedule.description,
              status: schedule.status,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.get(formatUrl(apiUrls.getLaneMinimumLocationTypes), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          success: true,
          message: "",
          errorCode: null,
          item: {
            locationTypes: mockLocationTypes,
          },
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(apiUrls.getLaneMinimumLocationValues),
      async ({ request }) => {
        await delay(config.delay);
        const url = new URL(request.url);
        const locationType = url.searchParams.get("locationType") || "";

        const locationValues =
          locationType in mockLocationValuesByType
            ? mockLocationValuesByType[locationType]
            : [];

        return HttpResponse.json(
          {
            success: true,
            message: "",
            errorCode: null,
            item: {
              locationValues,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.post(
      formatUrl(apiUrls.createLaneMinimumSchedule),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          name: string;
          description: string | null;
          status: string;
          laneMinimums: Array<{
            sequence: number;
            originType: number;
            originLocation: string;
            destinationType: number;
            destinationLocation: string;
            minimumCharge: number;
            direction: string;
          }>;
        };

        // Check if name already exists
        const nameExists = mockLaneMinimumSchedules.some(
          (schedule) => schedule.name.toLowerCase() === body.name.toLowerCase()
        );

        if (nameExists) {
          return HttpResponse.json(
            {
              success: false,
              message: "Lane minimum schedule with this name already exists",
              errorCode: "DUPLICATE_NAME",
              item: null,
            },
            { status: 400 }
          );
        }

        // Generate new ID
        const newId =
          Math.max(...mockLaneMinimumSchedules.map((s) => s.id)) + 1;

        // Create new schedule in list
        const newSchedule = {
          id: newId,
          name: body.name,
          description: body.description,
          status: body.status,
          laneMinimumCount: body.laneMinimums.length,
          createdBy: "Test User",
          createdOn: new Date().toLocaleDateString("en-US"),
          modifiedBy: "Test User",
          modifiedOn: new Date().toLocaleDateString("en-US"),
          isGlobal: false,
        };

        mockLaneMinimumSchedules.push(newSchedule);

        const successMessage =
          body.status === "Draft"
            ? `Lane minimum schedule '${body.name}' saved as draft successfully`
            : `Lane minimum schedule '${body.name}' created successfully`;

        return HttpResponse.json(
          {
            success: true,
            message: successMessage,
            errorCode: null,
            item: {
              id: newId,
              name: body.name,
              description: body.description,
              status: body.status,
              laneMinimumCount: body.laneMinimums.length,
            },
          },
          { status: 201 }
        );
      }
    ),

    http.post(formatUrl(apiUrls.getFuelScaleAccounts), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        code: string;
        page: number;
        pageSize: number;
        search?: string;
      };
      const { page, pageSize, search } = body;

      const mockAccounts = mockFuelScales.commonAccounts;

      // Filter by search if provided
      let filteredAccounts = mockAccounts;
      if (search) {
        const lowerSearch = search.toLowerCase();
        filteredAccounts = mockAccounts.filter(
          (account) =>
            account.accountName.toLowerCase().includes(lowerSearch) ||
            account.accountNumber.toLowerCase().includes(lowerSearch)
        );
      }

      // Apply pagination
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedAccounts = filteredAccounts.slice(startIndex, endIndex);

      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            accounts: paginatedAccounts,
            pagination: {
              totalRecords: filteredAccounts.length,
              totalPages: Math.ceil(filteredAccounts.length / pageSize),
              currentPage: page,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),

    http.post(
      formatUrl(
        apiUrls.getFuelScaleHistory(":fuelScaleId" as unknown as number)
      ),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          page: number;
          pageSize: number;
          search: string;
          sortBy: string;
          sortOrder: string;
        };
        const {
          page = 1,
          pageSize = 10,
          search = "",
          sortBy = "",
          sortOrder = "",
        } = body;

        // Get mock history data (already in correct format)
        let historyData = [...mockFuelScales.commonHistory];

        // Filter by search if provided (search in price or effectiveDate)
        if (search) {
          const lowerSearch = search.toLowerCase();
          historyData = historyData.filter(
            (item) =>
              item.price.includes(lowerSearch) ||
              item.effectiveDate.toLowerCase().includes(lowerSearch)
          );
        }

        // Sort if sortBy is provided
        if (sortBy) {
          historyData.sort((a, b) => {
            const getValue = (item: (typeof historyData)[0]) => {
              switch (sortBy) {
                case "price":
                  return parseFloat(item.price);
                case "effectiveDate":
                  return new Date(item.effectiveDate).getTime();
                default:
                  return 0;
              }
            };
            const aVal = getValue(a);
            const bVal = getValue(b);
            const comparison = aVal - bVal;
            return sortOrder === "desc" ? -comparison : comparison;
          });
        }

        // Paginate
        const totalRecords = historyData.length;
        const startIndex = (page - 1) * pageSize;
        const paginatedHistory = historyData.slice(
          startIndex,
          startIndex + pageSize
        );

        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: {
              history: paginatedHistory,
              pagination: {
                totalRecords,
                totalPages: Math.ceil(totalRecords / pageSize),
                currentPage: page,
                pageSize,
              },
            },
          },
          { status: 200 }
        );
      }
    ),

    http.get(
      formatUrl(apiUrls.getFuelScaleById(":fuelScaleId" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const { fuelScaleId } = params;

        // Find the fuel scale in mockFuelScales by id
        const fuelScale = mockFuelScales.fuelScales.find(
          (scale) => scale.id === Number(fuelScaleId)
        );

        if (!fuelScale) {
          return HttpResponse.json(
            {
              success: false,
              message: "Fuel scale not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        // Get detailed data from mockFuelSurchargeCodeDetails using ID
        if (!(fuelScale.id in mockFuelSurchargeCodeDetails)) {
          return HttpResponse.json(
            {
              success: false,
              message: "Fuel scale detail not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        const detailData = mockFuelSurchargeCodeDetails[fuelScale.id];

        return HttpResponse.json(
          {
            success: true,
            message: "Fuel scale details retrieved successfully",
            errorCode: null,
            item: detailData,
          },
          { status: 200 }
        );
      }
    ),

    http.patch(
      formatUrl(
        apiUrls.updateFuelScaleStatus(":fuelScaleId" as unknown as number)
      ),
      async ({ params, request }) => {
        await delay(config.delay);
        const { fuelScaleId } = params;
        const body = (await request.json()) as {
          status: "Active" | "Inactive";
        };

        // Find and update the fuel scale in both mock sources
        const fuelScale = mockFuelScales.fuelScales.find(
          (scale) => scale.id === Number(fuelScaleId)
        );

        if (!fuelScale) {
          return HttpResponse.json(
            {
              success: false,
              message: "Fuel scale not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        // Update status in list
        fuelScale.status = body.status;

        // Update status in detail if exists
        if (fuelScale.id in mockFuelSurchargeCodeDetails) {
          mockFuelSurchargeCodeDetails[fuelScale.id].status = body.status;
        }

        return HttpResponse.json(
          {
            success: true,
            message: `Fuel scale status updated to ${body.status} successfully`,
            errorCode: null,
            item: {
              id: fuelScale.id,
              status: body.status,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.post(formatUrl(apiUrls.createFuelScale), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        code: string;
        longDescription: string;
        status?: string;
        [key: string]: unknown;
      };

      // Check if code already exists
      const codeExists = mockFuelScales.fuelScales.some(
        (scale) => scale.code === body.code
      );

      if (codeExists) {
        return HttpResponse.json(
          {
            success: false,
            message: "Fuel scale code already exists",
            errorCode: "DUPLICATE_CODE",
            item: null,
          },
          { status: 400 }
        );
      }

      // Generate new ID
      const newId = Math.max(...mockFuelScales.fuelScales.map((s) => s.id)) + 1;

      // Add to list
      mockFuelScales.fuelScales.push({
        id: newId,
        code: body.code,
        longDescription: body.longDescription,
        fuelPrice: "NATIONAL",
        fuelPriceValue: "0.00",
        status: body.status || "Active",
      });

      // Add to details
      mockFuelSurchargeCodeDetails[newId] = {
        ...body,
        fuelPrice: "NATIONAL",
        fuelPriceValue: "0.00",
        numberOfGroups: 0,
        numberOfSequences: 0,
        createdDate: new Date().toLocaleDateString(),
      } as FuelScaleDetail;

      return HttpResponse.json(
        {
          success: true,
          message:
            body.status === "Draft"
              ? "Draft saved successfully"
              : "Fuel scale created successfully",
          errorCode: null,
          item: mockFuelSurchargeCodeDetails[newId],
        },
        { status: 201 }
      );
    }),

    http.put(
      formatUrl(apiUrls.updateFuelScale(":fuelScaleId" as unknown as number)),
      async ({ params, request }) => {
        await delay(config.delay);
        const { fuelScaleId } = params;
        const body = (await request.json()) as {
          longDescription: string;
          status?: string;
          [key: string]: unknown;
        };

        const fuelScale = mockFuelScales.fuelScales.find(
          (scale) => scale.id === Number(fuelScaleId)
        );

        if (!fuelScale) {
          return HttpResponse.json(
            {
              success: false,
              message: "Fuel scale not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        // Update list item
        fuelScale.longDescription = body.longDescription;
        fuelScale.status = body.status || fuelScale.status;

        // Update detail entry
        const detailEntry = mockFuelSurchargeCodeDetails[Number(fuelScaleId)];
        Object.assign(detailEntry, body);

        return HttpResponse.json(
          {
            success: true,
            message:
              body.status === "Draft"
                ? "Draft saved successfully"
                : "Fuel scale updated successfully",
            errorCode: null,
            item: detailEntry,
          },
          { status: 200 }
        );
      }
    ),

    http.get(
      formatUrl(
        apiUrls.getFuelScaleViewCriteria(":criteriaId" as unknown as number)
      ),
      async ({ params }) => {
        await delay(config.delay);
        const { criteriaId } = params;
        const criteriaIdNum = Number(criteriaId);

        // Get criteria from mock data
        const mockCriteria =
          criteriaIdNum in mockFuelScaleCriteria
            ? mockFuelScaleCriteria[criteriaIdNum]
            : undefined;

        if (!mockCriteria) {
          return HttpResponse.json(
            {
              success: false,
              message: "Criteria not found",
              errorCode: "NOT_FOUND",
              item: null,
            },
            { status: 404 }
          );
        }

        return HttpResponse.json(
          {
            success: true,
            message: "Criteria retrieved successfully",
            errorCode: null,
            item: mockCriteria,
          },
          { status: 200 }
        );
      }
    ),
    // GET /secured/api/v1/fuel-price - Fetch fuel price data
    http.get(formatUrl(apiUrls.getFuelPrice), async () => {
      await delay(config.delay);

      return HttpResponse.json(mockFuelPriceApiResponse, { status: 200 });
    }),

    // POST /secured/api/v1/fuel-price/history - Fetch fuel price history with pagination and sorting
    http.post(formatUrl(apiUrls.getFuelPriceHistory), async ({ request }) => {
      await delay(config.delay);

      const body = (await request.json()) as {
        fuelPriceId: number;
        page: number;
        pageSize: number;
        sortBy: "price" | "effectiveDate" | "modifiedOn" | "modifiedBy";
        sortOrder: "asc" | "desc";
        search?: string;
      };

      const { fuelPriceId, page, pageSize, sortBy, sortOrder, search } = body;

      // Get the mock data for this fuel price ID
      const mockResponse = mockFuelPriceHistoryResponses[fuelPriceId];
      // Get the history data and filter by search query if provided
      let filteredHistory = [...mockResponse.item.history];
      if (search) {
        const searchLower = search.toLowerCase();
        filteredHistory = filteredHistory.filter(
          (item) =>
            item.price.toLowerCase().includes(searchLower) ||
            item.effectiveDate.toLowerCase().includes(searchLower) ||
            item.modifiedOn.toLowerCase().includes(searchLower) ||
            item.modifiedBy.toLowerCase().includes(searchLower)
        );
      }

      // Sort the history data
      const sortedHistory = filteredHistory.sort((a, b) => {
        let comparison = 0;

        if (sortBy === "price") {
          const priceA = parseFloat(a.price);
          const priceB = parseFloat(b.price);
          comparison = priceA - priceB;
        } else if (sortBy === "effectiveDate") {
          const dateA = new Date(a.effectiveDate);
          const dateB = new Date(b.effectiveDate);
          comparison = dateA.getTime() - dateB.getTime();
        } else if (sortBy === "modifiedOn") {
          const dateA = new Date(a.modifiedOn);
          const dateB = new Date(b.modifiedOn);
          comparison = dateA.getTime() - dateB.getTime();
        } else {
          comparison = a.modifiedBy.localeCompare(b.modifiedBy);
        }

        return sortOrder === "desc" ? -comparison : comparison;
      });

      // Paginate the results
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedHistory = sortedHistory.slice(startIndex, endIndex);

      const totalRecords = sortedHistory.length;
      const totalPages = Math.ceil(totalRecords / pageSize);

      return HttpResponse.json(
        {
          success: true,
          message: "Fuel price history retrieved successfully",
          errorCode: null,
          item: {
            history: paginatedHistory,
            pagination: {
              currentPage: page,
              totalPages,
              totalRecords,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),

    http.patch(
      formatUrl(apiUrls.updateFuelPrice(":fuelPriceId" as unknown as number)),
      async ({ request, params }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          price: number;
          effectiveDate: string;
        };
        const { fuelPriceId } = params;

        // Mock successful update response
        return HttpResponse.json(
          {
            success: true,
            message: "Fuel price updated successfully",
            errorCode: null,
            item: {
              id: Number(fuelPriceId),
              price: body.price,
              effectiveDate: body.effectiveDate,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.post(
      formatUrl(apiUrls.retryFuelPrice(":fuelPriceId" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const { fuelPriceId } = params;

        // Mock successful retry response - simulate fetching fresh fuel price data
        return HttpResponse.json(
          {
            success: true,
            message: "Fuel price fetched successfully",
            errorCode: null,
            item: {
              id: Number(fuelPriceId),
              scope: "PADD5",
              currentPrice: 4.25,
              priceChange: 0.05,
              percentageChange: 1.19,
              lastUpdated: new Date().toLocaleDateString(),
              hasError: false,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.post(
      formatUrl(
        apiUrls.createRoute(
          ":unixTime" as unknown as number,
          ":sicId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(
          {
            success: true,
            message: null,
            errorCode: null,
            item: {
              statusCounts: mockShipmentsStatusCounts,
            },
          },
          { status: 200 }
        );
      }
    ),

    http.get(formatUrl(apiUrls.loadBoard.transitDashboardSummary), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockTransitDashboardSummary.default);
    }),

    http.get(
      formatUrl(apiUrls.loadBoard.transitDashboardShipments(":tripNumber")),
      async () => {
        await delay(config.delay);

        // Return mock data - in real scenario, you could filter by tripNumber
        return HttpResponse.json(
          mockTransitDashboardShipments.default.shipments
        );
      }
    ),

    http.get(
      formatUrl(
        apiUrls.loadBoard.transitDashboardTripDetails(
          ":tripNumber" as unknown as number
        )
      ),
      async ({ params }) => {
        await delay(config.delay);
        const body =
          params.tripNumber === "10016841"
            ? mockTransitDashboardTripDetailsWithLoadPair
            : mockTransitDashboardTripDetails.default;
        return HttpResponse.json(body);
      }
    ),

    // Trip Notes
    http.get(
      formatUrl(
        apiUrls.loadBoard.getTripNotes(":tripNumber" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockTripNotes.default);
      }
    ),

    // Trip History (status history)
    http.get(
      formatUrl(
        apiUrls.loadBoard.getTripHistory(":tripNumber" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockTripHistory);
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.loadBoard.deleteTripHistoryStatus(
          ":tripNumber" as unknown as number,
          ":statusId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.post(
      formatUrl(
        apiUrls.loadBoard.createTripNote(":tripNumber" as unknown as number)
      ),
      async ({ request, params }) => {
        await delay(config.delay);

        const requestSchema = z.object({
          content: z.string(),
        });

        const requestBody = requestSchema.parse(await request.json());
        const newAutoIncNoteId = getNextAutoIncValue(
          mockTripNotes.default.map((item) => item.id)
        );

        const tripNumber = params.tripNumber as unknown as string;
        const scheduleId = Number(tripNumber);
        const creatorId = crypto.randomUUID();

        const currentDate = dayjs();
        const newNote: TripNote = {
          id: newAutoIncNoteId,
          content: requestBody.content,
          createdBy: "Test User",
          createdOn: currentDate,
          modifiedBy: "Test User",
          modifiedOn: currentDate,
          isEdited: false,
          scheduleId,
          creatorId,
        };

        return HttpResponse.json(newNote, { status: 201 });
      }
    ),
    http.get(formatUrl(apiUrls.loadBoard.transitRecentUpdates), async () => {
      await delay(config.delay);
      return HttpResponse.json({ trips: mockRecentUpdates.default });
    }),

    http.get(
      formatUrl(apiUrls.loadBoard.transitDashboardNotifications(100)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockTripDashboardNotifications, {
          status: 200,
        });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.updateTripNote(":noteId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.loadBoard.deleteTripNote(":noteId" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    // Loadboard v2 Shipment Notes
    http.get(
      formatUrl(
        apiUrls.loadBoard.getShipmentNotes(":shipmentId" as unknown as number)
      ),
      async ({ params }) => {
        await delay(config.delay);
        const shipmentId = String(params.shipmentId);
        const notes = mockShipmentNotes[shipmentId] ?? [];
        return HttpResponse.json(notes);
      }
    ),

    http.post(
      formatUrl(
        apiUrls.loadBoard.createShipmentNote(":shipmentId" as unknown as number)
      ),
      async ({ request, params }) => {
        await delay(config.delay);

        const requestSchema = z.object({
          content: z.string(),
        });

        const requestBody = requestSchema.parse(await request.json());
        const shipmentId = String(params.shipmentId);
        const shipmentIdNum = Number(params.shipmentId);

        let existingNotes = mockShipmentNotes[shipmentId];
        if (!existingNotes) {
          existingNotes = [];
          mockShipmentNotes[shipmentId] = existingNotes;
        }

        const newAutoIncNoteId = getNextAutoIncValue(
          existingNotes.map((item) => item.id)
        );

        const creatorId = crypto.randomUUID();
        const currentDate = new Date();

        const newNote = {
          id: newAutoIncNoteId,
          shipmentId: shipmentIdNum,
          content: requestBody.content,
          createdBy: "Test User",
          createdOn: currentDate,
          modifiedBy: "Test User",
          modifiedOn: currentDate,
          isEdited: false,
          creatorId,
        };

        existingNotes.push(newNote);

        return HttpResponse.json(newNote, { status: 201 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.updateShipmentNote(
          ":shipmentId" as unknown as number,
          ":noteId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.delete(
      formatUrl(
        apiUrls.loadBoard.deleteShipmentNote(
          ":shipmentId" as unknown as number,
          ":noteId" as unknown as number
        )
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.text(null, { status: 200 });
      }
    ),

    http.put(
      formatUrl(
        apiUrls.loadBoard.tripCBOLInfo(":tripNumber" as unknown as number)
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json();
      }
    ),

    http.get(
      formatUrl(apiUrls.loadBoard.getTripsByCarrierInBulkRequest("*")),
      async ({ request }) => {
        const url = new URL(request.url);
        const carrierCode = url.searchParams.get("CarrierCode");

        if (!carrierCode) {
          await delay(config.delay);
          return HttpResponse.text("CarrierCode parameter is required", {
            status: 400,
          });
        }

        await delay(config.delay);

        // Return trips for the specified carrier, or empty if carrier not found
        const tripsData =
          carrierCode in mockBulkRequestTripsByCarrier
            ? mockBulkRequestTripsByCarrier[
                carrierCode as keyof typeof mockBulkRequestTripsByCarrier
              ]
            : { totalCount: 0, trips: [] };

        return HttpResponse.json(tripsData);
      }
    ),

    http.post(
      formatUrl(apiUrls.loadBoard.sendBulkRequest),
      async ({ request }) => {
        await delay(config.delay);

        const body = (await request.json()) as {
          tripNumbers?: number[];
        };

        if (!body.tripNumbers || !Array.isArray(body.tripNumbers)) {
          return HttpResponse.json(
            { error: "tripNumbers array is required" },
            { status: 400 }
          );
        }

        if (body.tripNumbers.length === 0) {
          return HttpResponse.json(
            { error: "At least one trip number is required" },
            { status: 400 }
          );
        }

        // Simulate successful bulk request
        return new HttpResponse(null, { status: 204 });
      }
    ),

    // * Employees (DockAuto)
    http.get(
      formatUrl(apiUrls.getEmployeeById(":employeeId")),
      async ({ params }) => {
        const { employeeId } = params;
        const employee = mockEmployees.employees.find(
          (e) => e.employeeId === employeeId
        );
        if (!employee) return new HttpResponse(null, { status: 404 });
        await delay(config.delay);
        return HttpResponse.json(employee);
      }
    ),
    http.get(formatUrl(apiUrls.getEmployees()), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockEmployees);
    }),
    http.put<
      object,
      { loginId: string; imageBase64?: string; imageExtension?: string }
    >(formatUrl(apiUrls.resetFaceId), async ({ request }) => {
      await delay(config.delay);
      const body = await request.json();
      const employee = mockEmployees.employees.find(
        (e) => e.employeeId === body.loginId
      );
      if (!employee) {
        return new HttpResponse(null, { status: 404 });
      }
      if (body.imageBase64 && body.imageExtension) {
        // Simulate uploading a new face image
        employee.pictureUrl = `https://mock-bucket.s3.amazonaws.com/face-images/${body.loginId}.${body.imageExtension}`;
        employee.pictureLastModified = new Date().toISOString();
      } else {
        // Simulate removing the face image
        employee.pictureUrl = null;
        employee.pictureLastModified = null;
      }
      return new HttpResponse(null, { status: 200 });
    }),

    http.post<object, OsadAssignment>(
      formatUrl(apiUrls.osad.createOSAD(":sicId" as unknown as number)),
      async ({ request }) => {
        await delay(config.delay);
        const body = await request.json();
        if (!body.reasonId) {
          return HttpResponse.json(
            { error: "Reason is required" },
            { status: 400 }
          );
        }

        if (body.shipmentIds.length === 0) {
          return HttpResponse.json(
            { error: "At least one shipment is required" },
            { status: 400 }
          );
        }

        return new HttpResponse(null, { status: 204 });
      }
    ),

    http.get(formatUrl(apiUrls.osad.getReasonCodes), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockReasonCodes.default);
    }),
    http.get(formatUrl("operations/trip-list"), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockTripList.default);
    }),

    http.get(formatUrl(apiUrls.getRegionsWithSicsV2), async () => {
      await delay(config.delay);
      return HttpResponse.json(mockRegions.default);
    }),

    http.get(formatUrl(apiUrls.getRegionsCount), async () => {
      await delay(config.delay);
      return HttpResponse.json(
        {
          loadboardRegionCount: mockRegions.default.length,
          ucOfdRegionCount: mockRegions.default.length,
        },
        { status: 200 }
      );
    }),

    http.get(
      formatUrl(apiUrls.getRegionById(":regionId" as unknown as number)),
      async ({ params }) => {
        await delay(config.delay);
        const { regionId } = params;
        const region = mockRegions.default.find(
          (r) => r.id === Number(regionId)
        );

        if (!region) {
          return HttpResponse.json(
            { error: "Region not found" },
            { status: 404 }
          );
        }

        return HttpResponse.json(region);
      }
    ),

    http.post(formatUrl(apiUrls.createRegionV2), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as { name: string };
      return HttpResponse.json(
        { id: 1, name: body.name, type: "Loadboard", serviceCenters: [] },
        { status: 201 }
      );
    }),

    http.put(
      formatUrl(
        apiUrls.unassignSicV2(":regionId" as unknown as number, ":sicCode")
      ),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    http.patch(
      formatUrl(apiUrls.updateRegionName(":regionId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    http.delete(
      formatUrl(apiUrls.deleteRegionV2(":regionId" as unknown as number)),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(null, { status: 200 });
      }
    ),

    // TODO(LH-2806): [FE][API] Integrate Table Data and Sorting API — MSW handler returns a static page; add query parsing for sort/pagination when backend contract exists.
    http.get(
      formatUrl(apiUrls.ucccOfd.ucccOfdDashboardSummary(":date")),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(mockUcccOfdDashboardSummary.default, {
          status: 200,
        });
      }
    ),

    // TODO(LH-2806): [FE][API] Integrate Table Data and Sorting API — returns static grouped payload;
    // add request query parsing (sort, page, pageSize, filters) and slice/filter responses when contract exists.
    http.get(formatUrl(apiUrls.ucccOfd.ucccOfdShipments(":date")), async () => {
      await delay(config.delay);
      const apiPayload = serializeUcccOfdShipmentsListForApi(
        mockUcccOfdShipments.default
      );
      ucccOfdShipmentsSchema.listResponse.parse(apiPayload);
      return HttpResponse.json(apiPayload, { status: 200 });
    }),

    http.get(
      formatUrl(apiUrls.ucccOfdServiceFailure.getServiceFailureReasonCodes),
      async () => {
        await delay(config.delay);
        return HttpResponse.json(
          { failureReasons: mockServiceFailureReasonCodes },
          { status: 200 }
        );
      }
    ),

    http.post(
      formatUrl(
        apiUrls.ucccOfdServiceFailure.updateServiceFailure(
          ":shipmentId" as unknown as number
        )
      ),
      async ({ request }) => {
        await delay(config.delay);
        const body = (await request.json()) as {
          failureReasonCode: string;
          subReason: string;
          explanation: string;
        };

        if (!body.failureReasonCode || !body.subReason || !body.explanation) {
          return HttpResponse.json(
            { error: "Missing required fields" },
            { status: 400 }
          );
        }

        // Return 204 No Content to match real API behavior
        return new HttpResponse(null, { status: 204 });
      }
    ),

    http.post(
      formatUrl(apiUrls.ucccOfd.expedite(":shipmentId")),
      async ({ request }) => {
        await delay(config.delay);
        await request.json();
        return new HttpResponse(null, { status: 204 });
      }
    ),

    // Fuel Surcharges List
    http.post(formatUrl(apiUrls.getFuelSurchargesList), async ({ request }) => {
      await delay(config.delay);
      const body = (await request.json()) as {
        page: number;
        pageSize: number;
        date?: string;
        sortBy?: string;
        sortOrder?: "asc" | "desc";
      };

      const { page = 1, pageSize = 10 } = body;

      // Calculate pagination
      const totalRecords = mockFuelSurcharges.length;
      const totalPages = Math.ceil(totalRecords / pageSize);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = mockFuelSurcharges.slice(startIndex, endIndex);

      return HttpResponse.json(
        {
          success: true,
          message: null,
          errorCode: null,
          item: {
            fuelSurcharges: paginatedData,
            pagination: {
              currentPage: page,
              totalPages,
              totalRecords,
              pageSize,
            },
          },
        },
        { status: 200 }
      );
    }),
  ];
}
